import React, { useRef, useState } from 'react';

import { VerifiedOutlined } from '@mui/icons-material';
import { Box, Divider, Typography, Rating, Popover } from '@mui/material';
import { BiBrain } from 'react-icons/bi';

import RouteLink from '@ivy/components/atoms/RouteLink';
import {
	Accreditation,
	accreditationLevels,
	accreditationNames,
	CmsMetric,
	ED_VOLUME_RANGE,
	ED_VOLUME_VERBOSE,
	StrokeCertLevel,
	STROKELVL2VERBOSE,
} from '@ivy/constants/facility';
import { OWNERSHIP_DESCRIPTION } from '@ivy/constants/organization';
import { type FacilityFeatures_FacilityFragment } from '@ivy/gql/types/graphql';
import { buildInternalLink } from '@ivy/lib/util/route';

// For AHA data, 1 = yes, 0 = no, and 2 = no
// const isAha1 = (value) => value === '1';
// const format012 = (value) => (isAha1(value) ? 'Yes' : 'No');
const checkNullUndefined = (value) => value != null || null;
// const checkNullUndefinedZero = (value) => (value != null ? value !== 0 : null);
const formatInt = (value) => value.toLocaleString();

// isFeatured returns one of three values. True if to show a checkmark, false to show cross, null to show unavailable.
// It defaults to true.
// https://docs.google.com/spreadsheets/d/1c-3YbUinLaUmnpTpccbjd-mjJADIBOXeyrmCi5HfbJc/edit#gid=845883208

export interface FacilityFieldSource {
	name: string;
	fullName: string;
	year: number | (() => number);
}

const sources = {
	AHA: {
		name: 'AHA',
		fullName: 'American Hospital Association',
		year: 2022,
	},
	CMS: {
		name: 'CMS',
		fullName: 'Centers for Medicare & Medicaid Services',
		year: () => new Date().getFullYear(),
	},
	IVY: {
		name: 'Ivy',
		fullName: 'Ivy Clinicians',
		year: () => new Date().getFullYear(),
	},
	EMRA: {
		name: 'EMRA',
		fullName: "Emergency Medicine Residents' Association",
		year: () => new Date().getFullYear(),
	},
	JC: {
		name: 'Joint Commission',
		fullName: 'Joint Commission',
		year: 2023,
	},
	ACEP: {
		name: 'ACEP',
		fullName: 'American College of Emergency Physicians',
		year: 2024,
	},
} satisfies Record<string, FacilityFieldSource>;

export interface FacilitySectionItem {
	label: string;
	getValue: (
		facility: FacilityFeatures_FacilityFragment,
	) => string | number | undefined | null;
	isFeatured?: (val: string | number | undefined | null) => boolean | null;
	customIcon?: React.ReactNode;
	formatter?: (
		val: string | number | undefined | null,
		facility: FacilityFeatures_FacilityFragment,
	) => React.ReactNode;
	link?: string;
	sources: FacilityFieldSource[];
}

export interface FacilitySectionConfig {
	section: string;
	items: FacilitySectionItem[];
	tooltip?: React.ReactNode;
}

interface EDVisitsProps {
	value: string | number | undefined | null;
	facility: FacilityFeatures_FacilityFragment;
}

const EDVisits = ({ value, facility }: EDVisitsProps) => {
	const ref = useRef<HTMLSpanElement>(null);
	const [open, setOpen] = useState(false);
	if (!value) {
		return null;
	}
	const numEntities = facility.cmsFacility?.facilities.length || 1;
	const edv =
		typeof value === 'string'
			? `${ED_VOLUME_VERBOSE[value]} (${ED_VOLUME_RANGE[value]})`
			: value;
	if (numEntities <= 1) {
		return <>{edv}</>;
	}
	return (
		<>
			<Typography
				ref={ref}
				component='span'
				fontWeight='bold'
				sx={{
					cursor: 'pointer',
					WebkitTapHighlightColor: 'transparent',
					color: (theme) => theme.palette.primary.main,
					'&:hover': {
						textDecoration: 'underline',
					},
				}}
				onClick={() => setOpen(true)}
			>
				{edv} across {formatInt(numEntities)} sites
			</Typography>
			<Popover
				open={open}
				PaperProps={{
					sx: {
						boxShadow: '0px 16px 40px rgba(142, 149, 155, 0.3)',
						maxWidth: 'calc(100% - 32px)',
					},
				}}
				anchorEl={ref.current}
				onClose={() => setOpen(false)}
				anchorOrigin={{
					horizontal: 'left',
					vertical: 'bottom',
				}}
				transformOrigin={{
					horizontal: 'left',
					vertical: 'top',
				}}
			>
				<Box
					p={{
						xs: 2,
						sm: 3,
					}}
				>
					{facility.cmsFacility?.facilities.map(({ id, name }, idx, self) => (
						<React.Fragment key={id}>
							<Typography variant='body1' fontWeight='bold'>
								{name}
							</Typography>
							{idx !== self.length - 1 && (
								<Divider
									sx={{
										my: {
											xs: 2,
											sm: 3,
										},
									}}
								/>
							)}
						</React.Fragment>
					))}
				</Box>
			</Popover>
		</>
	);
};

export const getFacilityFields = ({
	onClickShowEmployers,
}): FacilitySectionConfig[] => [
	{
		section: 'About',
		items: [
			{
				label: 'Facility Type',
				// Never not available
				getValue: (facility) => facility.facilityType,
				customIcon: (
					<svg
						width='32'
						height='33'
						viewBox='0 0 32 33'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<mask id='path-1-inside-1_1855_7581' fill='white'>
							<rect x='5.5' y='2.39291' width='21' height='29.1' rx='1' />
						</mask>
						<rect
							x='5.5'
							y='2.39291'
							width='21'
							height='29.1'
							rx='1'
							stroke='currentColor'
							strokeWidth='4'
							mask='url(#path-1-inside-1_1855_7581)'
						/>
						<mask id='path-2-inside-2_1855_7581' fill='white'>
							<rect x='12' y='21.4929' width='8' height='10' rx='1' />
						</mask>
						<rect
							x='12'
							y='21.4929'
							width='8'
							height='10'
							rx='1'
							stroke='currentColor'
							strokeWidth='4'
							mask='url(#path-2-inside-2_1855_7581)'
						/>
						<mask id='path-3-inside-3_1855_7581' fill='white'>
							<rect x='10' y='6.90112' width='5' height='6' rx='1' />
						</mask>
						<rect
							x='10'
							y='6.90112'
							width='5'
							height='6'
							rx='1'
							stroke='currentColor'
							strokeWidth='4'
							mask='url(#path-3-inside-3_1855_7581)'
						/>
						<mask id='path-4-inside-4_1855_7581' fill='white'>
							<rect x='17' y='6.90112' width='5' height='6' rx='1' />
						</mask>
						<rect
							x='17'
							y='6.90112'
							width='5'
							height='6'
							rx='1'
							stroke='currentColor'
							strokeWidth='4'
							mask='url(#path-4-inside-4_1855_7581)'
						/>
						<mask id='path-5-inside-5_1855_7581' fill='white'>
							<rect x='10' y='13.9011' width='5' height='6' rx='1' />
						</mask>
						<rect
							x='10'
							y='13.9011'
							width='5'
							height='6'
							rx='1'
							stroke='currentColor'
							strokeWidth='4'
							mask='url(#path-5-inside-5_1855_7581)'
						/>
						<mask id='path-6-inside-6_1855_7581' fill='white'>
							<rect x='17' y='13.9011' width='5' height='6' rx='1' />
						</mask>
						<rect
							x='17'
							y='13.9011'
							width='5'
							height='6'
							rx='1'
							stroke='currentColor'
							strokeWidth='4'
							mask='url(#path-6-inside-6_1855_7581)'
						/>
					</svg>
				),
				sources: [sources.CMS, sources.IVY],
			},
			{
				label: 'Employer Ownership Structure',
				// TODO: fix, wrong typing
				getValue: (facility) => {
					if (!facility.contracts.length) {
						return null;
					}
					if (facility.contracts.length === 1) {
						return (
							OWNERSHIP_DESCRIPTION[
								facility.contracts[0].org.ownershipStructure
							] || ''
						);
					}
					return (
						<Typography
							fontWeight='bold'
							sx={{
								cursor: 'pointer',
								WebkitTapHighlightColor: 'transparent',
								color: (theme) => theme.palette.primary.main,
								'&:hover': {
									textDecoration: 'underline',
								},
							}}
							onClick={onClickShowEmployers}
						>
							Multiple
						</Typography>
					);
				},
				// May be undefined if no current contract
				isFeatured: (value) => !!value || null,
				customIcon: (
					<svg
						width='33'
						height='34'
						viewBox='0 0 33 34'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<mask id='path-1-inside-1_2_439' fill='white'>
							<rect
								x='11.1031'
								y='2.65572'
								width='10.0329'
								height='10.312'
								rx='1'
							/>
						</mask>
						<rect
							x='11.1031'
							y='2.65572'
							width='10.0329'
							height='10.312'
							rx='1'
							stroke='currentColor'
							strokeWidth='4'
							mask='url(#path-1-inside-1_2_439)'
						/>
						<mask id='path-2-inside-2_2_439' fill='white'>
							<rect
								x='4.08005'
								y='20.1861'
								width='10.0329'
								height='10.312'
								rx='1'
							/>
						</mask>
						<rect
							x='4.08005'
							y='20.1861'
							width='10.0329'
							height='10.312'
							rx='1'
							stroke='currentColor'
							strokeWidth='4'
							mask='url(#path-2-inside-2_2_439)'
						/>
						<mask id='path-3-inside-3_2_439' fill='white'>
							<rect
								x='18.1262'
								y='20.1861'
								width='10.0329'
								height='10.312'
								rx='1'
							/>
						</mask>
						<rect
							x='18.1262'
							y='20.1861'
							width='10.0329'
							height='10.312'
							rx='1'
							stroke='currentColor'
							strokeWidth='4'
							mask='url(#path-3-inside-3_2_439)'
						/>
						<path
							d='M16.1196 12.9677V16.5769H9.09651M9.09651 16.5769V21.7329M9.09651 16.5769H23.1426V21.2173'
							stroke='currentColor'
							strokeWidth='2'
							strokeLinejoin='round'
						/>
					</svg>
				),
				sources: [sources.IVY],
			},
			{
				label: 'EM Residency',
				getValue: (facility) => {
					if (!facility.residencies?.length) {
						return 'None';
					}
					return facility.residencies.map((el, idx, self) => (
						<React.Fragment key={el.id}>
							<RouteLink
								to={{
									whitelabel: 'emra',
									pathname: buildInternalLink(
										RouteLink.routes.EMRA_RESIDENCY_SHOW,
										{
											trainingId: [el.training.id, el.training.slug],
										},
									),
								}}
								underline='hover'
								openInNewTab={true}
							>
								{el.training.name}
							</RouteLink>
							{idx < self.length - 1 && <br />}
						</React.Fragment>
					));
				},
				// Never not available
				isFeatured: () => true,
				customIcon: (
					<svg
						width='33'
						height='33'
						viewBox='0 0 33 33'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<path
							d='M3.08032 6.16315H16.4302V27.7284H3.08032C2.51317 27.7284 2.05341 27.2686 2.05341 26.7015V7.19006C2.05341 6.62291 2.51317 6.16315 3.08032 6.16315Z'
							stroke='currentColor'
							strokeWidth='2.05383'
						/>
						<path
							d='M29.7802 27.7285H16.4302V6.16321H29.7802C30.3473 6.16321 30.8071 6.62297 30.8071 7.19012V26.7015C30.8071 27.2687 30.3473 27.7285 29.7802 27.7285Z'
							stroke='currentColor'
							strokeWidth='2.05383'
						/>
						<path
							d='M20.0239 13.8613H27.2123'
							stroke='currentColor'
							strokeWidth='2.05383'
						/>
						<path
							d='M20.0239 16.9419H27.2123'
							stroke='currentColor'
							strokeWidth='2.05383'
						/>
						<path
							d='M20.0239 20.0229H27.2123'
							stroke='currentColor'
							strokeWidth='2.05383'
						/>
					</svg>
				),
				sources: [sources.EMRA],
			},
			{
				label: 'Annual ED Visits',
				getValue: (facility) =>
					facility.cmsFacility?.metrics.find((el) => el.measureId === 'EDV')
						?.score,
				formatter: (value, facility) => (
					<EDVisits value={value} facility={facility} />
				),
				isFeatured: (value) => !!value || null,
				customIcon: (
					<svg
						width='33'
						height='33'
						viewBox='0 0 33 33'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<circle
							opacity='0.7'
							cx='11.33'
							cy='5.99316'
							r='3'
							stroke='currentColor'
							strokeWidth='2'
						/>
						<g opacity='0.7'>
							<mask id='path-2-inside-1_2_2964' fill='white'>
								<path
									fillRule='evenodd'
									clipRule='evenodd'
									d='M8.32996 10.9932C6.6731 10.9932 5.32996 12.3363 5.32996 13.9932V18.5858C5.32996 19.8641 6.12955 20.9557 7.25588 21.3878V24.4746C7.25588 26.602 8.98041 28.3265 11.1077 28.3265C13.2351 28.3265 14.9596 26.602 14.9596 24.4746V21.3878C16.0859 20.9557 16.8855 19.8641 16.8855 18.5858V13.9932C16.8855 12.3363 15.5424 10.9932 13.8855 10.9932H8.32996Z'
								/>
							</mask>
							<path
								d='M7.25588 21.3878H9.25588V20.0128L7.97213 19.5204L7.25588 21.3878ZM14.9596 21.3878L14.2433 19.5204L12.9596 20.0128V21.3878H14.9596ZM7.32996 13.9932C7.32996 13.4409 7.77767 12.9932 8.32996 12.9932V8.99316C5.56853 8.99316 3.32996 11.2317 3.32996 13.9932H7.32996ZM7.32996 18.5858V13.9932H3.32996V18.5858H7.32996ZM7.97213 19.5204C7.59338 19.3751 7.32996 19.0087 7.32996 18.5858H3.32996C3.32996 20.7195 4.66572 22.5363 6.53964 23.2551L7.97213 19.5204ZM9.25588 24.4746V21.3878H5.25588V24.4746H9.25588ZM11.1077 26.3265C10.085 26.3265 9.25588 25.4974 9.25588 24.4746H5.25588C5.25588 27.7065 7.87585 30.3265 11.1077 30.3265V26.3265ZM12.9596 24.4746C12.9596 25.4974 12.1305 26.3265 11.1077 26.3265V30.3265C14.3396 30.3265 16.9596 27.7065 16.9596 24.4746H12.9596ZM12.9596 21.3878V24.4746H16.9596V21.3878H12.9596ZM14.8855 18.5858C14.8855 19.0087 14.6221 19.3751 14.2433 19.5204L15.6758 23.2551C17.5498 22.5363 18.8855 20.7195 18.8855 18.5858H14.8855ZM14.8855 13.9932V18.5858H18.8855V13.9932H14.8855ZM13.8855 12.9932C14.4378 12.9932 14.8855 13.4409 14.8855 13.9932H18.8855C18.8855 11.2317 16.6469 8.99316 13.8855 8.99316V12.9932ZM8.32996 12.9932H13.8855V8.99316H8.32996V12.9932Z'
								fill='currentColor'
								mask='url(#path-2-inside-1_2_2964)'
							/>
						</g>
						<circle
							cx='21.33'
							cy='7.99316'
							r='3'
							stroke='currentColor'
							strokeWidth='2'
						/>
						<mask id='path-5-inside-2_2_2964' fill='white'>
							<path
								fillRule='evenodd'
								clipRule='evenodd'
								d='M18.33 12.9932C16.6731 12.9932 15.33 14.3363 15.33 15.9932V20.9932C15.33 22.2994 16.1648 23.4106 17.33 23.8225V26.9932C17.33 29.2023 19.1208 30.9932 21.33 30.9932C23.5391 30.9932 25.33 29.2023 25.33 26.9932V23.8225C26.4951 23.4106 27.33 22.2994 27.33 20.9932V15.9932C27.33 14.3363 25.9868 12.9932 24.33 12.9932H18.33Z'
							/>
						</mask>
						<path
							d='M17.33 23.8225H19.33V22.4081L17.9964 21.9368L17.33 23.8225ZM25.33 23.8225L24.6635 21.9368L23.33 22.4081V23.8225H25.33ZM17.33 15.9932C17.33 15.4409 17.7777 14.9932 18.33 14.9932V10.9932C15.5685 10.9932 13.33 13.2317 13.33 15.9932H17.33ZM17.33 20.9932V15.9932H13.33V20.9932H17.33ZM17.9964 21.9368C17.6054 21.7985 17.33 21.4254 17.33 20.9932H13.33C13.33 23.1733 14.7242 25.0227 16.6635 25.7081L17.9964 21.9368ZM19.33 26.9932V23.8225H15.33V26.9932H19.33ZM21.33 28.9932C20.2254 28.9932 19.33 28.0977 19.33 26.9932H15.33C15.33 30.3069 18.0162 32.9932 21.33 32.9932V28.9932ZM23.33 26.9932C23.33 28.0977 22.4345 28.9932 21.33 28.9932V32.9932C24.6437 32.9932 27.33 30.3069 27.33 26.9932H23.33ZM23.33 23.8225V26.9932H27.33V23.8225H23.33ZM25.33 20.9932C25.33 21.4254 25.0546 21.7985 24.6635 21.9368L25.9964 25.7081C27.9357 25.0227 29.33 23.1733 29.33 20.9932H25.33ZM25.33 15.9932V20.9932H29.33V15.9932H25.33ZM24.33 14.9932C24.8822 14.9932 25.33 15.4409 25.33 15.9932H29.33C29.33 13.2317 27.0914 10.9932 24.33 10.9932V14.9932ZM18.33 14.9932H24.33V10.9932H18.33V14.9932Z'
							fill='currentColor'
							mask='url(#path-5-inside-2_2_2964)'
						/>
					</svg>
				),
				sources: [sources.CMS],
			},
			{
				// TODO: for hospitalists, this should only show the inpatient EHR, not the EDIS
				label: 'EHR',
				getValue: (facility) => facility.ehrs.map((el) => el.name).join(', '),
				// May be 1 (yes), 0 (no), null (unknown), or undefined (no aha facility)
				isFeatured: (value) => !!value || null,
				customIcon: (
					<svg
						width='32'
						height='26'
						viewBox='0 0 32 26'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<path
							d='M28.8 0H3.2C1.424 0 0 1.4625 0 3.25V22.75C0 24.5375 1.424 26 3.2 26H28.8C30.56 26 32 24.5375 32 22.75V3.25C32 1.4625 30.576 0 28.8 0ZM28.8 22.75H3.2V6.5H28.8V22.75ZM25.6 21.125H16V17.875H25.6V21.125ZM8.8 21.125L6.544 18.8337L10.672 14.625L6.528 10.4163L8.8 8.125L15.2 14.625L8.8 21.125Z'
							fill='currentColor'
						/>
					</svg>
				),
				sources: [sources.IVY],
			},
			{
				label: 'Stroke Certification',
				getValue: (facility) =>
					facility.strokeCerts
						.map((el) => el.level)
						.sort(
							(a, b) =>
								Object.values(StrokeCertLevel).indexOf(b) -
								Object.values(StrokeCertLevel).indexOf(a),
						)
						.map((el) => STROKELVL2VERBOSE[el])
						.join('\n') || 'None',
				isFeatured: (value) => !!value,
				customIcon: <BiBrain style={{ fontSize: '33px' }} />,
				sources: [sources.JC],
			},
			// {
			// 	label: 'Cath Lab',
			// 	getValue: (facility) => facility.ahaFacility?.svc1?.cathLab,
			// 	// May be 1 (yes), 0 (no), null (unknown), or undefined (no aha facility)
			// 	isFeatured: (value) => (value != null ? isAha1(value) : null),
			// 	formatter: format012,
			// 	customIcon: (
			// 		<svg
			// 			width='33'
			// 			height='30'
			// 			viewBox='0 0 33 30'
			// 			fill='none'
			// 			xmlns='http://www.w3.org/2000/svg'
			// 		>
			// 			<path
			// 				d='M8.82996 20.9971C10.9966 23.3304 15.93 27.9971 16.33 27.9971C16.73 27.9971 21.1633 23.3304 23.33 20.9971'
			// 				stroke='currentColor'
			// 				strokeWidth='2.2'
			// 				strokeLinecap='round'
			// 			/>
			// 			<path
			// 				d='M4.23 13.4969C4.23 13.4969 0.831344 6.99695 6.20421 4.20569C11.5771 1.41443 16.3307 5.99691 16.3307 5.99691C16.3307 5.99691 22.1687 0.446625 26.9992 4.72179C31.8298 8.99695 28.3299 13.4969 28.3299 13.4969'
			// 				stroke='currentColor'
			// 				strokeWidth='2.2'
			// 				strokeLinecap='round'
			// 			/>
			// 			<path
			// 				d='M3.32996 17.1735H10.8783L13.83 11.897L16.33 19.997L18.8461 14.497L20.9429 17.1735H29.33'
			// 				stroke='currentColor'
			// 				strokeWidth='2.2'
			// 				strokeLinecap='round'
			// 				strokeLinejoin='round'
			// 			/>
			// 		</svg>
			// 	),
			// 	sources: [sources.AHA],
			// },
			// {
			// 	label: 'Medical School Affiliation',
			// 	getValue: (facility) => facility.ahaFacility?.medSchool,
			// 	isFeatured: (value) => (value != null ? isAha1(value) : null),
			// 	formatter: format012,
			// 	customIcon: (
			// 		<svg
			// 			width='31'
			// 			height='29'
			// 			viewBox='0 0 31 29'
			// 			fill='none'
			// 			xmlns='http://www.w3.org/2000/svg'
			// 		>
			// 			<path
			// 				d='M17.101 1.26899L17.101 1.26901L29.0788 7.44277C29.7967 7.79401 30.2474 8.5259 30.2474 9.32161C30.2474 10.1202 29.7964 10.8493 29.079 11.203C29.0785 11.2032 29.0781 11.2034 29.0777 11.2036L26.4089 12.5797L26.4089 21.0213C26.4089 21.0215 26.4089 21.0216 26.4089 21.0217C26.4142 22.0549 25.8356 23.0002 24.9123 23.4634L24.9124 23.4633L24.8448 23.3294L24.9121 23.4635L17.101 1.26899ZM17.101 1.26899C16.0214 0.71294 14.7359 0.71294 13.6563 1.26899L13.6563 1.26901M17.101 1.26899L13.6563 1.26901M13.6563 1.26901L1.67854 7.44276M13.6563 1.26901L1.67854 7.44276M28.3632 9.79699L28.3632 9.79714C28.5493 9.71219 28.6688 9.526 28.6688 9.32161C28.6688 9.12017 28.5496 8.93377 28.3632 8.84869L28.3568 8.84573L28.3568 8.84558L16.3777 2.67371L16.3774 2.67358C15.7514 2.3496 15.0059 2.3496 14.3799 2.67358L14.3797 2.67371L2.40049 8.84558L2.39415 8.84885L2.39408 8.84869C2.20771 8.93377 2.08856 9.12017 2.08856 9.32161C2.08856 9.526 2.20801 9.71219 2.39408 9.79714L2.40056 9.8001L2.40048 9.80025L9.27675 13.3427L14.973 9.92496C14.9731 9.92493 14.9731 9.92491 14.9732 9.92489L14.9734 9.92476L15.0505 10.0534C15.3526 9.87109 15.7458 9.97005 15.9281 10.2721L28.3632 9.79699ZM28.3632 9.79699L28.3568 9.80025L16.3778 15.972M28.3632 9.79699L16.3778 15.972M16.3778 15.972C16.3778 15.9721 16.3778 15.9721 16.3777 15.9721C15.7515 16.2937 15.0057 16.2937 14.3795 15.972M16.3778 15.972H14.3795M14.3795 15.972L10.929 14.1944L15.7838 11.2811L15.784 11.281L15.7068 11.1523L14.3795 15.972ZM1.67854 7.44276C0.960663 7.794 0.509912 8.52589 0.509912 9.32161C0.509912 10.1202 0.960892 10.8492 1.67833 11.203L4.34845 12.5797V21.0217C4.3431 22.0549 4.92171 23.0002 5.84225 23.4633L1.67854 7.44276ZM10.747 25.9397C10.7633 25.9206 10.7794 25.9013 10.7952 25.8816L10.7953 25.8815C10.8082 25.8653 10.8169 25.8504 10.8223 25.8405L10.8282 25.8295L10.8302 25.8259L10.7016 25.7487L10.6808 25.7899L10.6339 25.8826L10.747 25.9397ZM7.06072 22.3087L6.55594 22.0524L6.55576 22.0524C6.1689 21.8566 5.9251 21.4584 5.9297 21.0237V21.0221V13.3933L8.1896 14.5589V19.4138H7.70011C7.48993 19.4109 7.28746 19.495 7.13978 19.6426L7.13865 19.6438C6.99238 19.7932 6.90835 19.992 6.90834 20.2018L6.90836 20.2032C6.91113 20.5134 7.09698 20.7878 7.37224 20.91L7.06072 22.3087ZM8.24634 24.2609L8.24646 24.2604L8.96672 21.014C8.96758 21.013 8.96869 21.012 8.97011 21.011C8.97365 21.0085 8.97652 21.0081 8.97762 21.0081C8.98163 21.0081 8.98561 21.0098 8.98836 21.0132L9.71141 24.2605L9.7115 24.2609C9.76105 24.4806 9.70777 24.7115 9.56573 24.8893C9.41702 25.0586 9.20381 25.156 8.97762 25.156C8.75343 25.156 8.53809 25.0582 8.39246 24.8898C8.25017 24.7118 8.19676 24.4807 8.24634 24.2609ZM14.588 17.7056V26.1116L11.3011 24.4502C11.3054 24.2725 11.29 24.0951 11.2549 23.9198L11.2549 23.9198L11.2543 23.9167L10.5856 20.9101C10.8608 20.7878 11.0467 20.5134 11.0495 20.2032V20.2018C11.0495 19.992 10.9655 19.7932 10.8192 19.6438L10.8181 19.6426C10.6704 19.495 10.4679 19.4109 10.2577 19.4138H9.76825V15.3724L13.6563 17.3768L13.6575 17.3774C13.9529 17.5263 14.2656 17.637 14.588 17.7056ZM24.8276 21.0221L24.8276 21.0237C24.8322 21.4584 24.5884 21.8566 24.2016 22.0524L16.1693 26.1116V17.7038C16.492 17.637 16.8047 17.5262 17.0998 17.3774L17.101 17.3768L24.8276 13.3932L24.8276 21.0221ZM5.84251 23.4634L6.70717 23.9004L6.70355 23.9168C6.46985 24.9741 6.99278 26.054 7.96984 26.5219L5.84251 23.4634Z'
			// 				fill='currentColor'
			// 				stroke='currentColor'
			// 				strokeWidth='0.3'
			// 			/>
			// 		</svg>
			// 	),
			// 	sources: [sources.AHA],
			// },
			// {
			// 	label: 'Health System',
			// 	getValue: (facility) => facility.ahaFacility?.healthSystem,
			// 	// Either a string, undefined, or null, never an empty string
			// 	// Note null means not featured in this case instead of not available
			// 	isFeatured: (value) =>
			// 		value === null ? false : value === undefined ? null : true,
			// 	formatter: (value) => value || 'None',
			// 	customIcon: (
			// 		<svg
			// 			width='26'
			// 			height='25'
			// 			viewBox='0 0 26 25'
			// 			fill='none'
			// 			xmlns='http://www.w3.org/2000/svg'
			// 		>
			// 			<path
			// 				d='M5.47368 9.72222C2.44947 9.72222 0 12.2083 0 15.2778C0 18.3472 2.44947 20.8333 5.47368 20.8333C8.49789 20.8333 10.9474 18.3472 10.9474 15.2778C10.9474 12.2083 8.49789 9.72222 5.47368 9.72222ZM5.47368 18.0556C3.96842 18.0556 2.73684 16.8056 2.73684 15.2778C2.73684 13.75 3.96842 12.5 5.47368 12.5C6.97895 12.5 8.21053 13.75 8.21053 15.2778C8.21053 16.8056 6.97895 18.0556 5.47368 18.0556ZM16.4347 16.6667C14.1768 16.6667 12.3295 18.5417 12.3295 20.8333C12.3295 23.125 14.1768 25 16.4347 25C18.6926 25 20.54 23.125 20.54 20.8333C20.54 18.5417 18.6926 16.6667 16.4347 16.6667V16.6667ZM16.4347 22.2222C15.6821 22.2222 15.0663 21.5972 15.0663 20.8333C15.0663 20.0694 15.6821 19.4444 16.4347 19.4444C17.1874 19.4444 17.8032 20.0694 17.8032 20.8333C17.8032 21.5972 17.1874 22.2222 16.4347 22.2222V22.2222ZM18.4737 0C14.3274 0 10.9474 3.43056 10.9474 7.63889C10.9474 11.8472 14.3274 15.2778 18.4737 15.2778C22.62 15.2778 26 11.8472 26 7.63889C26 3.43056 22.62 0 18.4737 0ZM18.4737 12.5C15.8326 12.5 13.6842 10.3194 13.6842 7.63889C13.6842 4.95833 15.8326 2.77778 18.4737 2.77778C21.1147 2.77778 23.2632 4.95833 23.2632 7.63889C23.2632 10.3194 21.1147 12.5 18.4737 12.5Z'
			// 				fill='currentColor'
			// 			/>
			// 		</svg>
			// 	),
			// 	sources: [sources.AHA],
			// },
		],
	},
	{
		section: 'ACEP Accreditation',
		tooltip: (
			<>
				<RouteLink
					to='https://www.acep.org/patient-care/accreditation-programs'
					openInNewTab
				>
					Click here
				</RouteLink>{' '}
				to learn more about the ACEP accreditation programs.
			</>
		),
		items: [
			{
				label: accreditationNames[Accreditation.ACEP_CUAP],
				link: 'https://www.acep.org/cuap',
				getValue: (facility) =>
					facility.accreditations.find(
						({ accreditation }) => accreditation === Accreditation.ACEP_CUAP,
					)
						? 'Yes'
						: 'No',
				customIcon: (
					<svg
						width='34'
						height='23'
						viewBox='0 0 34 23'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<path
							fillRule='evenodd'
							clipRule='evenodd'
							d='M2.5506 11.0849C5.8958 15.8769 10.957 18.9685 16.7169 18.9685C22.4033 18.9685 27.4087 15.9553 30.7543 11.2675L29.3151 9.84464C26.284 14.2217 21.7219 16.9685 16.7169 16.9685C11.6417 16.9685 7.02193 14.1441 3.99215 9.65959L2.5506 11.0849ZM1.55881 12.0654C5.16827 17.3831 10.6626 20.7385 16.7169 20.7385C22.7007 20.7385 28.1376 17.4607 31.7482 12.2503L33.1876 13.6734C29.2623 19.1944 23.3823 22.7385 16.7169 22.7385C9.97823 22.7385 4.04227 19.116 0.117458 13.4905L1.55881 12.0654ZM4.98373 8.67922C7.74934 12.6378 11.9358 15.1986 16.7169 15.1986C21.4241 15.1986 25.555 12.7164 28.3209 8.86168L26.8819 7.439C24.4305 10.9826 20.7431 13.1986 16.7169 13.1986C12.621 13.1986 8.87567 10.9052 6.42558 7.25366L4.98373 8.67922ZM7.41687 6.27358C9.6029 9.39882 12.9147 11.4286 16.7169 11.4286C20.4448 11.4286 23.7012 9.47744 25.8874 6.45568L24.4488 5.03336C22.5769 7.74363 19.7642 9.42861 16.7169 9.42861C13.6006 9.42861 10.7295 7.6664 8.85921 4.84754L7.41687 6.27358ZM9.85001 3.86793C11.4565 6.15985 13.8937 7.65866 16.7169 7.65866C19.4649 7.65866 21.8472 6.23866 23.4536 4.04942L22.0157 2.62771C20.7233 4.50475 18.7851 5.65866 16.7169 5.65866C14.581 5.65866 12.5837 4.42793 11.2933 2.44099L9.85001 3.86793ZM12.2832 1.46228C13.3102 2.92107 14.8729 3.88871 16.7169 3.88871C18.4839 3.88871 19.9926 3.00022 21.0192 1.64247L19.5825 0.222048C18.8694 1.26629 17.8054 1.88871 16.7169 1.88871C15.5642 1.88871 14.439 1.19071 13.7289 0.032918L12.2832 1.46228Z'
							fill='currentColor'
						/>
					</svg>
				),
				sources: [sources.ACEP],
			},
			{
				label: accreditationNames[Accreditation.ACEP_GEDA],
				link: 'https://www.acep.org/geda',
				getValue: (facility) =>
					facility.accreditations.find(
						({ accreditation }) => accreditation === Accreditation.ACEP_GEDA,
					)?.level,
				formatter: (value) =>
					value ? accreditationLevels[Accreditation.ACEP_GEDA]?.[value] : 'No',
				customIcon: (
					<Box
						component='img'
						src='https://assets.ivyclinicians.io/images/acep-geda-icon.png'
						sx={{
							width: '38px',
							height: 'auto',
						}}
					/>
				),
				sources: [sources.ACEP],
			},
			{
				label: accreditationNames[Accreditation.ACEP_PACED],
				link: 'https://www.acep.org/paced',
				getValue: (facility) =>
					facility.accreditations.find(
						({ accreditation }) => accreditation === Accreditation.ACEP_PACED,
					)?.level,
				formatter: (value) =>
					value ? accreditationLevels[Accreditation.ACEP_PACED]?.[value] : 'No',
				customIcon: (
					<Box
						component='img'
						src='https://assets.ivyclinicians.io/images/acep-paced-icon.png'
						sx={{
							width: '32px',
							height: 'auto',
						}}
					/>
				),
				sources: [sources.ACEP],
			},
			{
				label: accreditationNames[Accreditation.ACEP_EDAP],
				link: 'https://www.acep.org/edap',
				getValue: (facility) => {
					const accred = facility.accreditations.find(
						({ accreditation }) => accreditation === Accreditation.ACEP_EDAP,
					);
					return accred ? accred.level || 'Yes' : 'No';
				},
				customIcon: (
					<VerifiedOutlined
						sx={{
							width: '32px',
							height: 'auto',
						}}
					/>
				),
				sources: [sources.ACEP],
			},
		],
	},
	{
		section: 'CMS Quality Measures',
		items: [
			{
				label: 'Medicare Hospital Quality Rating',
				getValue: (facility) => facility.cmsFacility?.overallRating,
				// May be null or undefined
				isFeatured: checkNullUndefined,
				customIcon: (
					<Box
						component='img'
						src='https://assets.ivyclinicians.io/images/medicare-logo.png'
						sx={{
							width: '32px',
							height: 'auto',
						}}
					/>
				),
				formatter: (value) => (
					<Rating
						value={typeof value === 'string' ? Number.parseInt(value) : value}
						readOnly
					/>
				),
				sources: [sources.CMS],
			},
			{
				label: 'Median ED Discharge Length of Stay',
				getValue: (facility) =>
					facility.cmsFacility?.metrics.find(
						(el) => el.measureId === CmsMetric.STAY_LENGTH,
					)?.score,
				isFeatured: checkNullUndefined,
				formatter: (value) =>
					value != null
						? `${(typeof value === 'string'
								? Number.parseInt(value)
								: value
						  ).toLocaleString()} minutes`
						: '',
				customIcon: (
					<svg
						width='31'
						height='32'
						viewBox='0 0 31 32'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<path
							d='M23.0393 6.88653L23.0396 6.88654C23.1919 6.89121 23.3396 6.93983 23.4649 7.02654C23.5902 7.11325 23.6878 7.23435 23.7458 7.37524C23.8039 7.51614 23.82 7.67081 23.7921 7.82063C23.7643 7.97011 23.694 8.10838 23.5896 8.21887C23.5894 8.21913 23.5891 8.21938 23.5889 8.21964L17.2077 15.1324C17.334 15.4047 17.3998 15.7016 17.4 16.0026V16.0029C17.3993 16.4717 17.2396 16.9252 16.9487 17.2901L19.2211 21.3786C19.2211 21.3786 19.2211 21.3786 19.2211 21.3786C19.2716 21.4693 19.3036 21.5692 19.3155 21.6723C19.3274 21.7755 19.3188 21.88 19.2903 21.9798C19.2618 22.0797 19.214 22.1729 19.1494 22.2543C19.0849 22.3356 19.0049 22.4035 18.9142 22.4539C18.8234 22.5043 18.7236 22.5364 18.6204 22.5483C18.5172 22.5602 18.4128 22.5516 18.3129 22.5231C18.2131 22.4946 18.1198 22.4467 18.0385 22.3821C17.9571 22.3175 17.8893 22.2376 17.8389 22.1468L17.8389 22.1468L15.5688 18.0593C15.4895 18.0684 15.4096 18.0729 15.3297 18.0727M23.0393 6.88653L15.33 17.9227M23.0393 6.88653C22.9245 6.88322 22.8104 6.90497 22.7049 6.95027C22.5997 6.99542 22.5056 7.06287 22.4291 7.14796L23.0393 6.88653ZM15.3297 18.0727L15.33 17.9227M15.3297 18.0727C15.3298 18.0727 15.3299 18.0727 15.33 18.0727V17.9227M15.3297 18.0727C14.7808 18.0726 14.2544 17.8546 13.8663 17.4664C13.4781 17.0782 13.26 16.5517 13.26 16.0027C13.26 15.4537 13.4781 14.9272 13.8663 14.539C14.2545 14.1508 14.781 13.9327 15.33 13.9327H15.3301C15.5751 13.9329 15.818 13.9765 16.0473 14.0614M15.33 17.9227C14.8208 17.9227 14.3324 17.7204 13.9723 17.3604C13.6123 17.0003 13.41 16.5119 13.41 16.0027C13.41 15.4935 13.6123 15.0051 13.9723 14.6451C14.3324 14.285 14.8208 14.0827 15.33 14.0827C15.5905 14.0829 15.8482 14.136 16.0875 14.239M16.0473 14.0614L15.9773 14.1372L16.0875 14.239M16.0473 14.0614C16.0807 14.0738 16.1139 14.087 16.1468 14.1012L16.0875 14.239M16.0473 14.0614L22.4283 7.14883L22.54 7.24896L16.0875 14.239M15.33 1.13271C7.12509 1.13271 0.459985 7.79782 0.459985 16.0027C0.459985 24.2076 7.12509 30.8727 15.33 30.8727C23.5349 30.8727 30.2 24.2076 30.2 16.0027C30.2 7.79782 23.5349 1.13271 15.33 1.13271ZM15.33 2.71271C22.6774 2.71271 28.62 8.65527 28.62 16.0027C28.62 23.3502 22.6774 29.2927 15.33 29.2927C7.98254 29.2927 2.03999 23.3502 2.03999 16.0027C2.03999 8.65527 7.98254 2.71271 15.33 2.71271Z'
							fill='currentColor'
							stroke='currentColor'
							strokeWidth='0.3'
						/>
					</svg>
				),
				sources: [sources.CMS],
			},
			{
				label: 'Patients Who Leave Without Being Seen',
				getValue: (facility) =>
					facility.cmsFacility?.metrics.find(
						(el) => el.measureId === CmsMetric.LEFT_WITHOUT_SEEN,
					)?.score,
				isFeatured: checkNullUndefined,
				formatter: (value) =>
					value != null
						? `${(typeof value === 'string'
								? Number.parseInt(value)
								: value
						  ).toLocaleString()}%`
						: '',
				customIcon: (
					<svg
						width='21'
						height='30'
						viewBox='0 0 21 30'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<mask
							id='path-1-outside-1_3_2828'
							maskUnits='userSpaceOnUse'
							x='-0.27002'
							y='-0.717773'
							width='21'
							height='31'
							fill='black'
						>
							<rect
								fill='white'
								x='-0.27002'
								y='-0.717773'
								width='21'
								height='31'
							/>
							<path d='M11.61 0.282227C9.85029 0.282227 8.40998 1.72254 8.40998 3.48223C8.40998 5.24192 9.85029 6.68223 11.61 6.68223C13.3697 6.68223 14.81 5.24192 14.81 3.48223C14.81 1.72254 13.3697 0.282227 11.61 0.282227ZM11.61 1.56223C12.6778 1.56223 13.53 2.4144 13.53 3.48223C13.53 4.55006 12.6778 5.40223 11.61 5.40223C10.5422 5.40223 9.68998 4.55006 9.68998 3.48223C9.68998 2.4144 10.5422 1.56223 11.61 1.56223ZM9.18498 7.32223C8.48166 7.32223 7.79696 7.55391 7.23873 7.98098L3.42998 10.8985C3.40657 10.9165 3.38443 10.9361 3.36373 10.9572C3.14438 11.1355 2.96337 11.3628 2.83873 11.6272C2.81888 11.6582 2.80173 11.6908 2.78748 11.7247L0.97248 16.006C0.824909 16.2746 0.72998 16.5867 0.72998 16.9222C0.72998 17.975 1.5972 18.8422 2.64998 18.8422C3.49371 18.8422 4.20924 18.2854 4.46248 17.5235L6.03123 14.0047L6.75998 13.471C6.40906 14.9091 5.96998 16.7147 5.96998 16.7147C5.95606 16.769 5.94934 16.8249 5.94998 16.881C5.90096 17.0884 5.85343 17.3025 5.85123 17.546C5.8499 17.5518 5.84865 17.5576 5.84748 17.5635L4.93498 22.141L1.97873 25.9535C1.97576 25.9576 1.97285 25.9618 1.96998 25.966C1.75406 26.2584 1.36998 26.7679 1.36998 27.4822C1.36998 28.7118 2.38042 29.7222 3.60998 29.7222C4.50332 29.7222 5.11521 29.141 5.47873 28.696C5.48297 28.691 5.48714 28.686 5.49123 28.681L8.67748 24.5397C8.7292 24.4724 8.76702 24.3955 8.78873 24.3135L9.38373 22.0822L10.47 23.0735L11.6437 27.7885L11.6287 27.6947C11.648 27.8968 11.7031 28.1051 11.7837 28.3097C11.7918 28.3409 11.8785 28.6895 12.1787 29.0272C12.4921 29.3798 13.066 29.7174 13.8462 29.7222C15.073 29.7293 16.0287 28.6971 16.0287 27.4972C16.0287 27.2179 16.0016 26.913 15.8875 26.5747L15.9025 26.6247L14.6175 21.4722C14.5971 21.39 14.5605 21.3127 14.51 21.2447L11.6687 17.4335L12.4662 14.4985L13.4425 15.5822C13.5229 15.6717 13.6269 15.7367 13.7425 15.7697L17.3337 16.796H17.335H17.3362C17.3389 16.7967 17.3436 16.7982 17.3487 16.7997C17.36 16.803 17.3754 16.808 17.395 16.8135C17.4341 16.8244 17.4874 16.8386 17.5487 16.8535C17.6714 16.8833 17.796 16.9222 18.01 16.9222C19.0631 16.9222 19.9671 16.0581 19.93 14.9797C19.8944 13.9603 19.1971 13.3576 18.6162 13.181L18.6112 13.1797L15.6312 12.3047L13.2 8.85973C13.1815 8.83322 13.161 8.80814 13.1387 8.78473C12.6628 7.95878 11.7954 7.39114 10.79 7.34098C10.7673 7.33535 10.7213 7.3323 10.6825 7.32848C10.7164 7.33161 10.6763 7.32748 10.6737 7.32723C10.6653 7.32718 10.6584 7.32223 10.65 7.32223C10.536 7.32223 10.5818 7.32309 10.6412 7.32348L10.6337 7.32223H9.18498ZM9.18498 8.60223H10.6287H10.6337L10.6387 8.60348C10.7848 8.60348 10.7884 8.60223 10.65 8.60223C11.5412 8.60223 12.25 9.31099 12.25 10.2022C12.25 10.3404 12.2237 10.4857 12.1762 10.6485C12.1639 10.6905 12.156 10.7336 12.1525 10.7772L10.3525 17.3985C10.327 17.492 10.3231 17.5901 10.3411 17.6854C10.359 17.7807 10.3984 17.8706 10.4562 17.9485L13.4062 21.906L14.66 26.9347C14.6643 26.9512 14.6693 26.9674 14.675 26.9835C14.7452 27.1917 14.7487 27.2696 14.7487 27.4972C14.7487 28.065 14.3947 28.4454 13.8537 28.4422C13.4001 28.4394 13.2437 28.2996 13.135 28.1772C13.0262 28.0549 13.0112 27.9797 13.0112 27.9797C13.0022 27.9416 12.9896 27.9043 12.9737 27.8685C12.9393 27.7912 12.9139 27.6933 12.9025 27.5735C12.8994 27.5419 12.894 27.5105 12.8862 27.4797L11.665 22.5772C11.6345 22.4545 11.5684 22.3436 11.475 22.2585L8.26498 19.3297C8.22702 19.2952 8.18505 19.2654 8.13998 19.241C7.88053 19.0993 7.67745 18.8971 7.51248 18.671L7.53748 18.8672C7.52578 18.7763 7.49466 18.6889 7.44623 18.611L7.44248 18.6072C7.41922 18.5713 7.39242 18.5378 7.36248 18.5072L7.43373 18.5785C7.24485 18.2792 7.12998 17.9376 7.12998 17.5622C7.12998 17.4029 7.15878 17.24 7.20873 17.0585C7.21617 17.0297 7.2216 17.0005 7.22498 16.971C7.24613 16.8902 8.00111 14.0098 8.42248 12.0535C8.4498 11.9266 8.43791 11.7944 8.38838 11.6745C8.33885 11.5545 8.25402 11.4524 8.14514 11.3818C8.03627 11.3111 7.90849 11.2753 7.77875 11.2789C7.64902 11.2826 7.52346 11.3256 7.41873 11.4022L5.13873 13.0722C5.04883 13.1382 4.97773 13.2266 4.93248 13.3285L3.29373 17.0022C3.28067 17.0314 3.2698 17.0615 3.26123 17.0922C3.18575 17.3648 2.9489 17.5622 2.64998 17.5622C2.28836 17.5622 2.00998 17.2838 2.00998 16.9222C2.00998 16.803 2.04402 16.6942 2.10748 16.591C2.12422 16.5634 2.13884 16.5345 2.15123 16.5047L3.93373 12.2997L3.89248 12.3535C3.92799 12.3072 3.957 12.2563 3.97873 12.2022C4.02143 12.096 4.09547 12.0023 4.19373 11.9297C4.24142 11.8941 4.28391 11.8521 4.31998 11.8047L4.27498 11.8635L8.01623 8.99848L8.01748 8.99723C8.35242 8.74143 8.76373 8.60223 9.18498 8.60223ZM13.3425 11.281L14.7137 13.2247C14.797 13.3427 14.9177 13.429 15.0562 13.4697L18.2437 14.406C18.3195 14.429 18.6344 14.5773 18.65 15.0247C18.6615 15.3595 18.3712 15.6422 18.01 15.6422C18.8301 15.6422 17.937 15.6305 17.8512 15.6097C17.8083 15.5993 17.7677 15.5878 17.7387 15.5797C17.7242 15.5757 17.7128 15.5733 17.705 15.571C17.7015 15.57 17.6985 15.5691 17.6962 15.5685H17.695L17.6912 15.5672L17.6875 15.566L14.2712 14.5897L12.8662 13.0285L13.3425 11.281ZM6.71873 19.7372C6.93269 19.9632 7.17661 20.1556 7.44248 20.311L8.31748 21.1097L7.58498 23.861L4.48623 27.8872C4.24434 28.1828 3.89121 28.4422 3.60998 28.4422C3.07186 28.4422 2.64998 28.0203 2.64998 27.4822C2.64998 27.3075 2.82736 26.9596 2.99748 26.7285L2.99873 26.726L6.03873 22.806C6.09961 22.7279 6.14154 22.6368 6.16123 22.5397L6.71873 19.7372Z' />
						</mask>
						<path
							d='M11.61 0.282227C9.85029 0.282227 8.40998 1.72254 8.40998 3.48223C8.40998 5.24192 9.85029 6.68223 11.61 6.68223C13.3697 6.68223 14.81 5.24192 14.81 3.48223C14.81 1.72254 13.3697 0.282227 11.61 0.282227ZM11.61 1.56223C12.6778 1.56223 13.53 2.4144 13.53 3.48223C13.53 4.55006 12.6778 5.40223 11.61 5.40223C10.5422 5.40223 9.68998 4.55006 9.68998 3.48223C9.68998 2.4144 10.5422 1.56223 11.61 1.56223ZM9.18498 7.32223C8.48166 7.32223 7.79696 7.55391 7.23873 7.98098L3.42998 10.8985C3.40657 10.9165 3.38443 10.9361 3.36373 10.9572C3.14438 11.1355 2.96337 11.3628 2.83873 11.6272C2.81888 11.6582 2.80173 11.6908 2.78748 11.7247L0.97248 16.006C0.824909 16.2746 0.72998 16.5867 0.72998 16.9222C0.72998 17.975 1.5972 18.8422 2.64998 18.8422C3.49371 18.8422 4.20924 18.2854 4.46248 17.5235L6.03123 14.0047L6.75998 13.471C6.40906 14.9091 5.96998 16.7147 5.96998 16.7147C5.95606 16.769 5.94934 16.8249 5.94998 16.881C5.90096 17.0884 5.85343 17.3025 5.85123 17.546C5.8499 17.5518 5.84865 17.5576 5.84748 17.5635L4.93498 22.141L1.97873 25.9535C1.97576 25.9576 1.97285 25.9618 1.96998 25.966C1.75406 26.2584 1.36998 26.7679 1.36998 27.4822C1.36998 28.7118 2.38042 29.7222 3.60998 29.7222C4.50332 29.7222 5.11521 29.141 5.47873 28.696C5.48297 28.691 5.48714 28.686 5.49123 28.681L8.67748 24.5397C8.7292 24.4724 8.76702 24.3955 8.78873 24.3135L9.38373 22.0822L10.47 23.0735L11.6437 27.7885L11.6287 27.6947C11.648 27.8968 11.7031 28.1051 11.7837 28.3097C11.7918 28.3409 11.8785 28.6895 12.1787 29.0272C12.4921 29.3798 13.066 29.7174 13.8462 29.7222C15.073 29.7293 16.0287 28.6971 16.0287 27.4972C16.0287 27.2179 16.0016 26.913 15.8875 26.5747L15.9025 26.6247L14.6175 21.4722C14.5971 21.39 14.5605 21.3127 14.51 21.2447L11.6687 17.4335L12.4662 14.4985L13.4425 15.5822C13.5229 15.6717 13.6269 15.7367 13.7425 15.7697L17.3337 16.796H17.335H17.3362C17.3389 16.7967 17.3436 16.7982 17.3487 16.7997C17.36 16.803 17.3754 16.808 17.395 16.8135C17.4341 16.8244 17.4874 16.8386 17.5487 16.8535C17.6714 16.8833 17.796 16.9222 18.01 16.9222C19.0631 16.9222 19.9671 16.0581 19.93 14.9797C19.8944 13.9603 19.1971 13.3576 18.6162 13.181L18.6112 13.1797L15.6312 12.3047L13.2 8.85973C13.1815 8.83322 13.161 8.80814 13.1387 8.78473C12.6628 7.95878 11.7954 7.39114 10.79 7.34098C10.7673 7.33535 10.7213 7.3323 10.6825 7.32848C10.7164 7.33161 10.6763 7.32748 10.6737 7.32723C10.6653 7.32718 10.6584 7.32223 10.65 7.32223C10.536 7.32223 10.5818 7.32309 10.6412 7.32348L10.6337 7.32223H9.18498ZM9.18498 8.60223H10.6287H10.6337L10.6387 8.60348C10.7848 8.60348 10.7884 8.60223 10.65 8.60223C11.5412 8.60223 12.25 9.31099 12.25 10.2022C12.25 10.3404 12.2237 10.4857 12.1762 10.6485C12.1639 10.6905 12.156 10.7336 12.1525 10.7772L10.3525 17.3985C10.327 17.492 10.3231 17.5901 10.3411 17.6854C10.359 17.7807 10.3984 17.8706 10.4562 17.9485L13.4062 21.906L14.66 26.9347C14.6643 26.9512 14.6693 26.9674 14.675 26.9835C14.7452 27.1917 14.7487 27.2696 14.7487 27.4972C14.7487 28.065 14.3947 28.4454 13.8537 28.4422C13.4001 28.4394 13.2437 28.2996 13.135 28.1772C13.0262 28.0549 13.0112 27.9797 13.0112 27.9797C13.0022 27.9416 12.9896 27.9043 12.9737 27.8685C12.9393 27.7912 12.9139 27.6933 12.9025 27.5735C12.8994 27.5419 12.894 27.5105 12.8862 27.4797L11.665 22.5772C11.6345 22.4545 11.5684 22.3436 11.475 22.2585L8.26498 19.3297C8.22702 19.2952 8.18505 19.2654 8.13998 19.241C7.88053 19.0993 7.67745 18.8971 7.51248 18.671L7.53748 18.8672C7.52578 18.7763 7.49466 18.6889 7.44623 18.611L7.44248 18.6072C7.41922 18.5713 7.39242 18.5378 7.36248 18.5072L7.43373 18.5785C7.24485 18.2792 7.12998 17.9376 7.12998 17.5622C7.12998 17.4029 7.15878 17.24 7.20873 17.0585C7.21617 17.0297 7.2216 17.0005 7.22498 16.971C7.24613 16.8902 8.00111 14.0098 8.42248 12.0535C8.4498 11.9266 8.43791 11.7944 8.38838 11.6745C8.33885 11.5545 8.25402 11.4524 8.14514 11.3818C8.03627 11.3111 7.90849 11.2753 7.77875 11.2789C7.64902 11.2826 7.52346 11.3256 7.41873 11.4022L5.13873 13.0722C5.04883 13.1382 4.97773 13.2266 4.93248 13.3285L3.29373 17.0022C3.28067 17.0314 3.2698 17.0615 3.26123 17.0922C3.18575 17.3648 2.9489 17.5622 2.64998 17.5622C2.28836 17.5622 2.00998 17.2838 2.00998 16.9222C2.00998 16.803 2.04402 16.6942 2.10748 16.591C2.12422 16.5634 2.13884 16.5345 2.15123 16.5047L3.93373 12.2997L3.89248 12.3535C3.92799 12.3072 3.957 12.2563 3.97873 12.2022C4.02143 12.096 4.09547 12.0023 4.19373 11.9297C4.24142 11.8941 4.28391 11.8521 4.31998 11.8047L4.27498 11.8635L8.01623 8.99848L8.01748 8.99723C8.35242 8.74143 8.76373 8.60223 9.18498 8.60223ZM13.3425 11.281L14.7137 13.2247C14.797 13.3427 14.9177 13.429 15.0562 13.4697L18.2437 14.406C18.3195 14.429 18.6344 14.5773 18.65 15.0247C18.6615 15.3595 18.3712 15.6422 18.01 15.6422C18.8301 15.6422 17.937 15.6305 17.8512 15.6097C17.8083 15.5993 17.7677 15.5878 17.7387 15.5797C17.7242 15.5757 17.7128 15.5733 17.705 15.571C17.7015 15.57 17.6985 15.5691 17.6962 15.5685H17.695L17.6912 15.5672L17.6875 15.566L14.2712 14.5897L12.8662 13.0285L13.3425 11.281ZM6.71873 19.7372C6.93269 19.9632 7.17661 20.1556 7.44248 20.311L8.31748 21.1097L7.58498 23.861L4.48623 27.8872C4.24434 28.1828 3.89121 28.4422 3.60998 28.4422C3.07186 28.4422 2.64998 28.0203 2.64998 27.4822C2.64998 27.3075 2.82736 26.9596 2.99748 26.7285L2.99873 26.726L6.03873 22.806C6.09961 22.7279 6.14154 22.6368 6.16123 22.5397L6.71873 19.7372Z'
							fill='currentColor'
						/>
						<path
							d='M11.61 0.282227C9.85029 0.282227 8.40998 1.72254 8.40998 3.48223C8.40998 5.24192 9.85029 6.68223 11.61 6.68223C13.3697 6.68223 14.81 5.24192 14.81 3.48223C14.81 1.72254 13.3697 0.282227 11.61 0.282227ZM11.61 1.56223C12.6778 1.56223 13.53 2.4144 13.53 3.48223C13.53 4.55006 12.6778 5.40223 11.61 5.40223C10.5422 5.40223 9.68998 4.55006 9.68998 3.48223C9.68998 2.4144 10.5422 1.56223 11.61 1.56223ZM9.18498 7.32223C8.48166 7.32223 7.79696 7.55391 7.23873 7.98098L3.42998 10.8985C3.40657 10.9165 3.38443 10.9361 3.36373 10.9572C3.14438 11.1355 2.96337 11.3628 2.83873 11.6272C2.81888 11.6582 2.80173 11.6908 2.78748 11.7247L0.97248 16.006C0.824909 16.2746 0.72998 16.5867 0.72998 16.9222C0.72998 17.975 1.5972 18.8422 2.64998 18.8422C3.49371 18.8422 4.20924 18.2854 4.46248 17.5235L6.03123 14.0047L6.75998 13.471C6.40906 14.9091 5.96998 16.7147 5.96998 16.7147C5.95606 16.769 5.94934 16.8249 5.94998 16.881C5.90096 17.0884 5.85343 17.3025 5.85123 17.546C5.8499 17.5518 5.84865 17.5576 5.84748 17.5635L4.93498 22.141L1.97873 25.9535C1.97576 25.9576 1.97285 25.9618 1.96998 25.966C1.75406 26.2584 1.36998 26.7679 1.36998 27.4822C1.36998 28.7118 2.38042 29.7222 3.60998 29.7222C4.50332 29.7222 5.11521 29.141 5.47873 28.696C5.48297 28.691 5.48714 28.686 5.49123 28.681L8.67748 24.5397C8.7292 24.4724 8.76702 24.3955 8.78873 24.3135L9.38373 22.0822L10.47 23.0735L11.6437 27.7885L11.6287 27.6947C11.648 27.8968 11.7031 28.1051 11.7837 28.3097C11.7918 28.3409 11.8785 28.6895 12.1787 29.0272C12.4921 29.3798 13.066 29.7174 13.8462 29.7222C15.073 29.7293 16.0287 28.6971 16.0287 27.4972C16.0287 27.2179 16.0016 26.913 15.8875 26.5747L15.9025 26.6247L14.6175 21.4722C14.5971 21.39 14.5605 21.3127 14.51 21.2447L11.6687 17.4335L12.4662 14.4985L13.4425 15.5822C13.5229 15.6717 13.6269 15.7367 13.7425 15.7697L17.3337 16.796H17.335H17.3362C17.3389 16.7967 17.3436 16.7982 17.3487 16.7997C17.36 16.803 17.3754 16.808 17.395 16.8135C17.4341 16.8244 17.4874 16.8386 17.5487 16.8535C17.6714 16.8833 17.796 16.9222 18.01 16.9222C19.0631 16.9222 19.9671 16.0581 19.93 14.9797C19.8944 13.9603 19.1971 13.3576 18.6162 13.181L18.6112 13.1797L15.6312 12.3047L13.2 8.85973C13.1815 8.83322 13.161 8.80814 13.1387 8.78473C12.6628 7.95878 11.7954 7.39114 10.79 7.34098C10.7673 7.33535 10.7213 7.3323 10.6825 7.32848C10.7164 7.33161 10.6763 7.32748 10.6737 7.32723C10.6653 7.32718 10.6584 7.32223 10.65 7.32223C10.536 7.32223 10.5818 7.32309 10.6412 7.32348L10.6337 7.32223H9.18498ZM9.18498 8.60223H10.6287H10.6337L10.6387 8.60348C10.7848 8.60348 10.7884 8.60223 10.65 8.60223C11.5412 8.60223 12.25 9.31099 12.25 10.2022C12.25 10.3404 12.2237 10.4857 12.1762 10.6485C12.1639 10.6905 12.156 10.7336 12.1525 10.7772L10.3525 17.3985C10.327 17.492 10.3231 17.5901 10.3411 17.6854C10.359 17.7807 10.3984 17.8706 10.4562 17.9485L13.4062 21.906L14.66 26.9347C14.6643 26.9512 14.6693 26.9674 14.675 26.9835C14.7452 27.1917 14.7487 27.2696 14.7487 27.4972C14.7487 28.065 14.3947 28.4454 13.8537 28.4422C13.4001 28.4394 13.2437 28.2996 13.135 28.1772C13.0262 28.0549 13.0112 27.9797 13.0112 27.9797C13.0022 27.9416 12.9896 27.9043 12.9737 27.8685C12.9393 27.7912 12.9139 27.6933 12.9025 27.5735C12.8994 27.5419 12.894 27.5105 12.8862 27.4797L11.665 22.5772C11.6345 22.4545 11.5684 22.3436 11.475 22.2585L8.26498 19.3297C8.22702 19.2952 8.18505 19.2654 8.13998 19.241C7.88053 19.0993 7.67745 18.8971 7.51248 18.671L7.53748 18.8672C7.52578 18.7763 7.49466 18.6889 7.44623 18.611L7.44248 18.6072C7.41922 18.5713 7.39242 18.5378 7.36248 18.5072L7.43373 18.5785C7.24485 18.2792 7.12998 17.9376 7.12998 17.5622C7.12998 17.4029 7.15878 17.24 7.20873 17.0585C7.21617 17.0297 7.2216 17.0005 7.22498 16.971C7.24613 16.8902 8.00111 14.0098 8.42248 12.0535C8.4498 11.9266 8.43791 11.7944 8.38838 11.6745C8.33885 11.5545 8.25402 11.4524 8.14514 11.3818C8.03627 11.3111 7.90849 11.2753 7.77875 11.2789C7.64902 11.2826 7.52346 11.3256 7.41873 11.4022L5.13873 13.0722C5.04883 13.1382 4.97773 13.2266 4.93248 13.3285L3.29373 17.0022C3.28067 17.0314 3.2698 17.0615 3.26123 17.0922C3.18575 17.3648 2.9489 17.5622 2.64998 17.5622C2.28836 17.5622 2.00998 17.2838 2.00998 16.9222C2.00998 16.803 2.04402 16.6942 2.10748 16.591C2.12422 16.5634 2.13884 16.5345 2.15123 16.5047L3.93373 12.2997L3.89248 12.3535C3.92799 12.3072 3.957 12.2563 3.97873 12.2022C4.02143 12.096 4.09547 12.0023 4.19373 11.9297C4.24142 11.8941 4.28391 11.8521 4.31998 11.8047L4.27498 11.8635L8.01623 8.99848L8.01748 8.99723C8.35242 8.74143 8.76373 8.60223 9.18498 8.60223ZM13.3425 11.281L14.7137 13.2247C14.797 13.3427 14.9177 13.429 15.0562 13.4697L18.2437 14.406C18.3195 14.429 18.6344 14.5773 18.65 15.0247C18.6615 15.3595 18.3712 15.6422 18.01 15.6422C18.8301 15.6422 17.937 15.6305 17.8512 15.6097C17.8083 15.5993 17.7677 15.5878 17.7387 15.5797C17.7242 15.5757 17.7128 15.5733 17.705 15.571C17.7015 15.57 17.6985 15.5691 17.6962 15.5685H17.695L17.6912 15.5672L17.6875 15.566L14.2712 14.5897L12.8662 13.0285L13.3425 11.281ZM6.71873 19.7372C6.93269 19.9632 7.17661 20.1556 7.44248 20.311L8.31748 21.1097L7.58498 23.861L4.48623 27.8872C4.24434 28.1828 3.89121 28.4422 3.60998 28.4422C3.07186 28.4422 2.64998 28.0203 2.64998 27.4822C2.64998 27.3075 2.82736 26.9596 2.99748 26.7285L2.99873 26.726L6.03873 22.806C6.09961 22.7279 6.14154 22.6368 6.16123 22.5397L6.71873 19.7372Z'
							stroke='currentColor'
							strokeWidth='0.4'
							mask='url(#path-1-outside-1_3_2828)'
						/>
					</svg>
				),
				sources: [sources.CMS],
			},
			{
				label:
					'Patients Who Receive Appropriate Care for Severe Sepsis and Septic Shock',
				getValue: (facility) =>
					facility.cmsFacility?.metrics.find(
						(el) => el.measureId === CmsMetric.SEPSIS,
					)?.score,
				isFeatured: checkNullUndefined,
				formatter: (value) =>
					value != null
						? `${(typeof value === 'string'
								? Number.parseInt(value)
								: value
						  ).toLocaleString()}%`
						: '',
				customIcon: (
					<svg
						width='33'
						height='33'
						viewBox='0 0 33 33'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<g clipPath='url(#clip0_3_505)'>
							<mask
								id='path-1-outside-1_3_505'
								maskUnits='userSpaceOnUse'
								x='0.609985'
								y='0.282227'
								width='31'
								height='31'
								fill='black'
							>
								<rect
									fill='white'
									x='0.609985'
									y='0.282227'
									width='31'
									height='31'
								/>
								<path d='M8.00999 1.28223C5.78749 1.28223 4.33499 2.20973 3.48999 2.98223L3.44999 3.02223C1.65249 4.89223 1.60999 7.31973 1.60999 7.68223C1.60999 9.05223 2.72999 10.2422 4.16999 10.2422C5.53999 10.2422 6.72999 9.12223 6.72999 7.68223C6.72999 7.32473 6.80499 6.99223 6.96999 6.78223C7.13499 6.57223 7.38499 6.40223 8.00999 6.40223C9.42499 6.40223 10.57 5.25723 10.57 3.84223C10.57 2.42723 9.42499 1.28223 8.00999 1.28223ZM27.85 1.92223C26.4425 1.92223 25.29 3.07473 25.29 4.48223C25.29 5.88973 26.4425 7.04223 27.85 7.04223C29.2575 7.04223 30.41 5.88973 30.41 4.48223C30.41 3.07473 29.2575 1.92223 27.85 1.92223ZM8.00999 2.56223C8.77249 2.56223 9.28999 3.07973 9.28999 3.84223C9.28999 4.60473 8.77249 5.12223 8.00999 5.12223C7.09999 5.12223 6.38249 5.45473 5.96999 5.98223C5.55749 6.50973 5.44999 7.14223 5.44999 7.68223C5.44999 8.41973 4.84749 8.96223 4.16999 8.96223C3.43249 8.96223 2.88999 8.35973 2.88999 7.68223C2.88999 7.53473 2.96749 5.37973 4.34999 3.92223C4.35749 3.91473 4.36249 3.90973 4.36999 3.90223C5.06249 3.27223 6.14999 2.56223 8.00999 2.56223ZM27.85 3.20223C28.565 3.20223 29.13 3.76723 29.13 4.48223C29.13 5.19723 28.565 5.76223 27.85 5.76223C27.135 5.76223 26.57 5.19723 26.57 4.48223C26.57 3.76723 27.135 3.20223 27.85 3.20223ZM17.51 5.12223C17.2 5.12473 16.885 5.14223 16.57 5.18223C12.9525 5.59973 9.98749 8.44473 9.42999 12.0622C9.15249 13.6572 9.35499 15.2372 9.90999 16.6222C10.505 18.1697 10.605 19.7547 9.92999 21.1622V21.1822C9.52249 22.0647 9.21249 23.0747 9.34999 24.1822C9.35249 24.1972 9.36749 24.2072 9.36999 24.2222C9.68249 27.5847 12.8825 29.8672 16.21 29.4422H16.23C21.415 28.6822 25.93 23.1722 25.93 13.4422C25.93 9.13973 22.6325 5.56973 18.43 5.16223C18.13 5.13223 17.82 5.11973 17.51 5.12223ZM17.53 6.40223C17.795 6.39973 18.055 6.41723 18.31 6.44223C21.885 6.78223 24.65 9.79223 24.65 13.4422C24.65 22.7847 20.465 27.5147 16.05 28.1622C13.3775 28.5047 10.8525 26.7097 10.63 24.0822V24.0422C10.525 23.2497 10.74 22.4872 11.09 21.7222C11.0925 21.7172 11.0875 21.7072 11.09 21.7022C11.9425 19.9147 11.7925 17.9372 11.11 16.1622V16.1422C10.64 14.9672 10.455 13.6097 10.69 12.2622C11.1575 9.22473 13.6925 6.81223 16.73 6.46223C17 6.42973 17.265 6.40473 17.53 6.40223ZM17.61 10.2422C15.85 10.2422 14.41 11.6822 14.41 13.4422C14.41 15.2022 15.85 16.6422 17.61 16.6422C19.37 16.6422 20.81 15.2022 20.81 13.4422C20.81 11.6822 19.37 10.2422 17.61 10.2422ZM17.61 11.5222C18.6775 11.5222 19.53 12.3747 19.53 13.4422C19.53 14.5097 18.6775 15.3622 17.61 15.3622C16.5425 15.3622 15.69 14.5097 15.69 13.4422C15.69 12.3747 16.5425 11.5222 17.61 11.5222ZM3.48999 15.3822C3.24499 15.3997 3.00249 15.4522 2.76999 15.5622C2.75749 15.5672 2.74249 15.5547 2.72999 15.5622C2.05999 15.8697 1.69749 16.5097 1.60999 17.1222V17.2222C1.60999 17.3997 1.62249 17.7672 1.82999 18.1422C1.83999 18.1522 1.84499 18.1722 1.84999 18.1822C1.85999 18.1972 1.83999 18.2072 1.84999 18.2222L1.86999 18.2022C2.34749 19.1222 3.51249 20.5847 5.60999 21.0222V21.0422C5.62499 21.0472 5.63499 21.0397 5.64999 21.0422C6.68749 21.2997 7.71499 20.6297 7.98999 19.6222V19.5822C8.16999 18.5972 7.56999 17.5097 6.48999 17.3022C6.47249 17.2972 6.46749 17.2872 6.44999 17.2822C6.07249 17.1822 5.78249 16.9747 5.58999 16.7822C5.50249 16.6947 5.43249 16.6247 5.38999 16.5622L5.36999 16.5222C5.36249 16.5097 5.35499 16.5122 5.34999 16.5022C5.34749 16.4972 5.35249 16.4872 5.34999 16.4822C5.34249 16.4622 5.32999 16.4597 5.32999 16.4622L5.30999 16.4222C5.07249 15.9497 4.67999 15.6197 4.20999 15.4622C3.98249 15.3872 3.73499 15.3647 3.48999 15.3822ZM3.56999 16.6222C3.65499 16.6172 3.73249 16.6372 3.80999 16.6622C3.96499 16.7147 4.08749 16.8347 4.16999 17.0022C4.17999 17.0222 4.19999 17.0447 4.20999 17.0622C4.23999 17.1222 4.26499 17.1772 4.30999 17.2422C4.39749 17.3722 4.52499 17.5372 4.68999 17.7022C5.01749 18.0297 5.50499 18.3822 6.18999 18.5422H6.20999L6.24999 18.5622C6.65249 18.6297 6.79749 18.9072 6.74999 19.2822C6.74999 19.2897 6.74999 19.2947 6.74999 19.3022C6.63749 19.6797 6.27999 19.8972 5.92999 19.8022H5.90999L5.88999 19.7822C4.21999 19.4472 3.25749 18.1772 2.94999 17.5622L2.92999 17.5422L2.90999 17.5022C2.92249 17.5222 2.89999 17.4047 2.88999 17.3022C2.92749 17.0322 3.07249 16.8072 3.26999 16.7222L3.28999 16.7022H3.30999C3.39249 16.6597 3.48499 16.6272 3.56999 16.6222ZM15.69 21.1222C14.6375 21.1222 13.77 21.9897 13.77 23.0422C13.77 24.0947 14.6375 24.9622 15.69 24.9622C16.7425 24.9622 17.61 24.0947 17.61 23.0422C17.61 21.9897 16.7425 21.1222 15.69 21.1222ZM15.69 22.4022C16.05 22.4022 16.33 22.6822 16.33 23.0422C16.33 23.4022 16.05 23.6822 15.69 23.6822C15.33 23.6822 15.05 23.4022 15.05 23.0422C15.05 22.6822 15.33 22.4022 15.69 22.4022ZM28.39 22.4022C27.4925 22.4347 26.7525 23.1297 26.59 24.0222H26.57C26.385 25.0997 26.15 25.8822 25.95 26.3822C25.85 26.6322 25.7725 26.8047 25.71 26.9222C25.68 26.9797 25.645 27.0197 25.63 27.0422L25.59 27.1022C25.025 27.9522 25.23 29.1247 26.05 29.7622C26.415 30.0372 26.8475 30.1422 27.21 30.1422C27.7875 30.1422 28.3175 29.8822 28.69 29.4422C28.695 29.4347 28.705 29.4297 28.71 29.4222C28.7175 29.4147 28.7225 29.4097 28.73 29.4022C28.87 29.2547 28.8975 29.1597 29.01 28.9622C29.1325 28.7422 29.29 28.4472 29.45 28.0622C29.7725 27.2897 30.145 26.1672 30.41 24.6822V24.6222C30.4975 23.5797 29.8175 22.6097 28.79 22.4222C28.6575 22.3972 28.5175 22.3972 28.39 22.4022ZM28.53 23.6622H28.57C28.94 23.7297 29.1525 24.0197 29.13 24.4822C28.8825 25.8647 28.555 26.8822 28.27 27.5622C28.1275 27.9047 27.9875 28.1722 27.89 28.3422C27.8175 28.4697 27.74 28.5822 27.75 28.5822L27.73 28.6022C27.59 28.7747 27.38 28.8622 27.21 28.8622C27.06 28.8622 26.9775 28.8522 26.83 28.7422C26.4975 28.4847 26.4475 28.1247 26.65 27.8222C26.665 27.7997 26.6925 27.7497 26.71 27.7222C26.745 27.6672 26.785 27.6047 26.83 27.5222C26.9175 27.3597 27.0325 27.1347 27.15 26.8422C27.3825 26.2547 27.65 25.4047 27.85 24.2422H27.83C27.8975 23.8697 28.205 23.6272 28.53 23.6622ZM5.44999 26.8822C4.39749 26.8822 3.52999 27.7497 3.52999 28.8022C3.52999 29.8547 4.39749 30.7222 5.44999 30.7222C6.50249 30.7222 7.36999 29.8547 7.36999 28.8022C7.36999 27.7497 6.50249 26.8822 5.44999 26.8822ZM5.44999 28.1622C5.80999 28.1622 6.08999 28.4422 6.08999 28.8022C6.08999 29.1622 5.80999 29.4422 5.44999 29.4422C5.08999 29.4422 4.80999 29.1622 4.80999 28.8022C4.80999 28.4422 5.08999 28.1622 5.44999 28.1622Z' />
							</mask>
							<path
								d='M8.00999 1.28223C5.78749 1.28223 4.33499 2.20973 3.48999 2.98223L3.44999 3.02223C1.65249 4.89223 1.60999 7.31973 1.60999 7.68223C1.60999 9.05223 2.72999 10.2422 4.16999 10.2422C5.53999 10.2422 6.72999 9.12223 6.72999 7.68223C6.72999 7.32473 6.80499 6.99223 6.96999 6.78223C7.13499 6.57223 7.38499 6.40223 8.00999 6.40223C9.42499 6.40223 10.57 5.25723 10.57 3.84223C10.57 2.42723 9.42499 1.28223 8.00999 1.28223ZM27.85 1.92223C26.4425 1.92223 25.29 3.07473 25.29 4.48223C25.29 5.88973 26.4425 7.04223 27.85 7.04223C29.2575 7.04223 30.41 5.88973 30.41 4.48223C30.41 3.07473 29.2575 1.92223 27.85 1.92223ZM8.00999 2.56223C8.77249 2.56223 9.28999 3.07973 9.28999 3.84223C9.28999 4.60473 8.77249 5.12223 8.00999 5.12223C7.09999 5.12223 6.38249 5.45473 5.96999 5.98223C5.55749 6.50973 5.44999 7.14223 5.44999 7.68223C5.44999 8.41973 4.84749 8.96223 4.16999 8.96223C3.43249 8.96223 2.88999 8.35973 2.88999 7.68223C2.88999 7.53473 2.96749 5.37973 4.34999 3.92223C4.35749 3.91473 4.36249 3.90973 4.36999 3.90223C5.06249 3.27223 6.14999 2.56223 8.00999 2.56223ZM27.85 3.20223C28.565 3.20223 29.13 3.76723 29.13 4.48223C29.13 5.19723 28.565 5.76223 27.85 5.76223C27.135 5.76223 26.57 5.19723 26.57 4.48223C26.57 3.76723 27.135 3.20223 27.85 3.20223ZM17.51 5.12223C17.2 5.12473 16.885 5.14223 16.57 5.18223C12.9525 5.59973 9.98749 8.44473 9.42999 12.0622C9.15249 13.6572 9.35499 15.2372 9.90999 16.6222C10.505 18.1697 10.605 19.7547 9.92999 21.1622V21.1822C9.52249 22.0647 9.21249 23.0747 9.34999 24.1822C9.35249 24.1972 9.36749 24.2072 9.36999 24.2222C9.68249 27.5847 12.8825 29.8672 16.21 29.4422H16.23C21.415 28.6822 25.93 23.1722 25.93 13.4422C25.93 9.13973 22.6325 5.56973 18.43 5.16223C18.13 5.13223 17.82 5.11973 17.51 5.12223ZM17.53 6.40223C17.795 6.39973 18.055 6.41723 18.31 6.44223C21.885 6.78223 24.65 9.79223 24.65 13.4422C24.65 22.7847 20.465 27.5147 16.05 28.1622C13.3775 28.5047 10.8525 26.7097 10.63 24.0822V24.0422C10.525 23.2497 10.74 22.4872 11.09 21.7222C11.0925 21.7172 11.0875 21.7072 11.09 21.7022C11.9425 19.9147 11.7925 17.9372 11.11 16.1622V16.1422C10.64 14.9672 10.455 13.6097 10.69 12.2622C11.1575 9.22473 13.6925 6.81223 16.73 6.46223C17 6.42973 17.265 6.40473 17.53 6.40223ZM17.61 10.2422C15.85 10.2422 14.41 11.6822 14.41 13.4422C14.41 15.2022 15.85 16.6422 17.61 16.6422C19.37 16.6422 20.81 15.2022 20.81 13.4422C20.81 11.6822 19.37 10.2422 17.61 10.2422ZM17.61 11.5222C18.6775 11.5222 19.53 12.3747 19.53 13.4422C19.53 14.5097 18.6775 15.3622 17.61 15.3622C16.5425 15.3622 15.69 14.5097 15.69 13.4422C15.69 12.3747 16.5425 11.5222 17.61 11.5222ZM3.48999 15.3822C3.24499 15.3997 3.00249 15.4522 2.76999 15.5622C2.75749 15.5672 2.74249 15.5547 2.72999 15.5622C2.05999 15.8697 1.69749 16.5097 1.60999 17.1222V17.2222C1.60999 17.3997 1.62249 17.7672 1.82999 18.1422C1.83999 18.1522 1.84499 18.1722 1.84999 18.1822C1.85999 18.1972 1.83999 18.2072 1.84999 18.2222L1.86999 18.2022C2.34749 19.1222 3.51249 20.5847 5.60999 21.0222V21.0422C5.62499 21.0472 5.63499 21.0397 5.64999 21.0422C6.68749 21.2997 7.71499 20.6297 7.98999 19.6222V19.5822C8.16999 18.5972 7.56999 17.5097 6.48999 17.3022C6.47249 17.2972 6.46749 17.2872 6.44999 17.2822C6.07249 17.1822 5.78249 16.9747 5.58999 16.7822C5.50249 16.6947 5.43249 16.6247 5.38999 16.5622L5.36999 16.5222C5.36249 16.5097 5.35499 16.5122 5.34999 16.5022C5.34749 16.4972 5.35249 16.4872 5.34999 16.4822C5.34249 16.4622 5.32999 16.4597 5.32999 16.4622L5.30999 16.4222C5.07249 15.9497 4.67999 15.6197 4.20999 15.4622C3.98249 15.3872 3.73499 15.3647 3.48999 15.3822ZM3.56999 16.6222C3.65499 16.6172 3.73249 16.6372 3.80999 16.6622C3.96499 16.7147 4.08749 16.8347 4.16999 17.0022C4.17999 17.0222 4.19999 17.0447 4.20999 17.0622C4.23999 17.1222 4.26499 17.1772 4.30999 17.2422C4.39749 17.3722 4.52499 17.5372 4.68999 17.7022C5.01749 18.0297 5.50499 18.3822 6.18999 18.5422H6.20999L6.24999 18.5622C6.65249 18.6297 6.79749 18.9072 6.74999 19.2822C6.74999 19.2897 6.74999 19.2947 6.74999 19.3022C6.63749 19.6797 6.27999 19.8972 5.92999 19.8022H5.90999L5.88999 19.7822C4.21999 19.4472 3.25749 18.1772 2.94999 17.5622L2.92999 17.5422L2.90999 17.5022C2.92249 17.5222 2.89999 17.4047 2.88999 17.3022C2.92749 17.0322 3.07249 16.8072 3.26999 16.7222L3.28999 16.7022H3.30999C3.39249 16.6597 3.48499 16.6272 3.56999 16.6222ZM15.69 21.1222C14.6375 21.1222 13.77 21.9897 13.77 23.0422C13.77 24.0947 14.6375 24.9622 15.69 24.9622C16.7425 24.9622 17.61 24.0947 17.61 23.0422C17.61 21.9897 16.7425 21.1222 15.69 21.1222ZM15.69 22.4022C16.05 22.4022 16.33 22.6822 16.33 23.0422C16.33 23.4022 16.05 23.6822 15.69 23.6822C15.33 23.6822 15.05 23.4022 15.05 23.0422C15.05 22.6822 15.33 22.4022 15.69 22.4022ZM28.39 22.4022C27.4925 22.4347 26.7525 23.1297 26.59 24.0222H26.57C26.385 25.0997 26.15 25.8822 25.95 26.3822C25.85 26.6322 25.7725 26.8047 25.71 26.9222C25.68 26.9797 25.645 27.0197 25.63 27.0422L25.59 27.1022C25.025 27.9522 25.23 29.1247 26.05 29.7622C26.415 30.0372 26.8475 30.1422 27.21 30.1422C27.7875 30.1422 28.3175 29.8822 28.69 29.4422C28.695 29.4347 28.705 29.4297 28.71 29.4222C28.7175 29.4147 28.7225 29.4097 28.73 29.4022C28.87 29.2547 28.8975 29.1597 29.01 28.9622C29.1325 28.7422 29.29 28.4472 29.45 28.0622C29.7725 27.2897 30.145 26.1672 30.41 24.6822V24.6222C30.4975 23.5797 29.8175 22.6097 28.79 22.4222C28.6575 22.3972 28.5175 22.3972 28.39 22.4022ZM28.53 23.6622H28.57C28.94 23.7297 29.1525 24.0197 29.13 24.4822C28.8825 25.8647 28.555 26.8822 28.27 27.5622C28.1275 27.9047 27.9875 28.1722 27.89 28.3422C27.8175 28.4697 27.74 28.5822 27.75 28.5822L27.73 28.6022C27.59 28.7747 27.38 28.8622 27.21 28.8622C27.06 28.8622 26.9775 28.8522 26.83 28.7422C26.4975 28.4847 26.4475 28.1247 26.65 27.8222C26.665 27.7997 26.6925 27.7497 26.71 27.7222C26.745 27.6672 26.785 27.6047 26.83 27.5222C26.9175 27.3597 27.0325 27.1347 27.15 26.8422C27.3825 26.2547 27.65 25.4047 27.85 24.2422H27.83C27.8975 23.8697 28.205 23.6272 28.53 23.6622ZM5.44999 26.8822C4.39749 26.8822 3.52999 27.7497 3.52999 28.8022C3.52999 29.8547 4.39749 30.7222 5.44999 30.7222C6.50249 30.7222 7.36999 29.8547 7.36999 28.8022C7.36999 27.7497 6.50249 26.8822 5.44999 26.8822ZM5.44999 28.1622C5.80999 28.1622 6.08999 28.4422 6.08999 28.8022C6.08999 29.1622 5.80999 29.4422 5.44999 29.4422C5.08999 29.4422 4.80999 29.1622 4.80999 28.8022C4.80999 28.4422 5.08999 28.1622 5.44999 28.1622Z'
								fill='currentColor'
							/>
							<path
								d='M8.00999 1.28223C5.78749 1.28223 4.33499 2.20973 3.48999 2.98223L3.44999 3.02223C1.65249 4.89223 1.60999 7.31973 1.60999 7.68223C1.60999 9.05223 2.72999 10.2422 4.16999 10.2422C5.53999 10.2422 6.72999 9.12223 6.72999 7.68223C6.72999 7.32473 6.80499 6.99223 6.96999 6.78223C7.13499 6.57223 7.38499 6.40223 8.00999 6.40223C9.42499 6.40223 10.57 5.25723 10.57 3.84223C10.57 2.42723 9.42499 1.28223 8.00999 1.28223ZM27.85 1.92223C26.4425 1.92223 25.29 3.07473 25.29 4.48223C25.29 5.88973 26.4425 7.04223 27.85 7.04223C29.2575 7.04223 30.41 5.88973 30.41 4.48223C30.41 3.07473 29.2575 1.92223 27.85 1.92223ZM8.00999 2.56223C8.77249 2.56223 9.28999 3.07973 9.28999 3.84223C9.28999 4.60473 8.77249 5.12223 8.00999 5.12223C7.09999 5.12223 6.38249 5.45473 5.96999 5.98223C5.55749 6.50973 5.44999 7.14223 5.44999 7.68223C5.44999 8.41973 4.84749 8.96223 4.16999 8.96223C3.43249 8.96223 2.88999 8.35973 2.88999 7.68223C2.88999 7.53473 2.96749 5.37973 4.34999 3.92223C4.35749 3.91473 4.36249 3.90973 4.36999 3.90223C5.06249 3.27223 6.14999 2.56223 8.00999 2.56223ZM27.85 3.20223C28.565 3.20223 29.13 3.76723 29.13 4.48223C29.13 5.19723 28.565 5.76223 27.85 5.76223C27.135 5.76223 26.57 5.19723 26.57 4.48223C26.57 3.76723 27.135 3.20223 27.85 3.20223ZM17.51 5.12223C17.2 5.12473 16.885 5.14223 16.57 5.18223C12.9525 5.59973 9.98749 8.44473 9.42999 12.0622C9.15249 13.6572 9.35499 15.2372 9.90999 16.6222C10.505 18.1697 10.605 19.7547 9.92999 21.1622V21.1822C9.52249 22.0647 9.21249 23.0747 9.34999 24.1822C9.35249 24.1972 9.36749 24.2072 9.36999 24.2222C9.68249 27.5847 12.8825 29.8672 16.21 29.4422H16.23C21.415 28.6822 25.93 23.1722 25.93 13.4422C25.93 9.13973 22.6325 5.56973 18.43 5.16223C18.13 5.13223 17.82 5.11973 17.51 5.12223ZM17.53 6.40223C17.795 6.39973 18.055 6.41723 18.31 6.44223C21.885 6.78223 24.65 9.79223 24.65 13.4422C24.65 22.7847 20.465 27.5147 16.05 28.1622C13.3775 28.5047 10.8525 26.7097 10.63 24.0822V24.0422C10.525 23.2497 10.74 22.4872 11.09 21.7222C11.0925 21.7172 11.0875 21.7072 11.09 21.7022C11.9425 19.9147 11.7925 17.9372 11.11 16.1622V16.1422C10.64 14.9672 10.455 13.6097 10.69 12.2622C11.1575 9.22473 13.6925 6.81223 16.73 6.46223C17 6.42973 17.265 6.40473 17.53 6.40223ZM17.61 10.2422C15.85 10.2422 14.41 11.6822 14.41 13.4422C14.41 15.2022 15.85 16.6422 17.61 16.6422C19.37 16.6422 20.81 15.2022 20.81 13.4422C20.81 11.6822 19.37 10.2422 17.61 10.2422ZM17.61 11.5222C18.6775 11.5222 19.53 12.3747 19.53 13.4422C19.53 14.5097 18.6775 15.3622 17.61 15.3622C16.5425 15.3622 15.69 14.5097 15.69 13.4422C15.69 12.3747 16.5425 11.5222 17.61 11.5222ZM3.48999 15.3822C3.24499 15.3997 3.00249 15.4522 2.76999 15.5622C2.75749 15.5672 2.74249 15.5547 2.72999 15.5622C2.05999 15.8697 1.69749 16.5097 1.60999 17.1222V17.2222C1.60999 17.3997 1.62249 17.7672 1.82999 18.1422C1.83999 18.1522 1.84499 18.1722 1.84999 18.1822C1.85999 18.1972 1.83999 18.2072 1.84999 18.2222L1.86999 18.2022C2.34749 19.1222 3.51249 20.5847 5.60999 21.0222V21.0422C5.62499 21.0472 5.63499 21.0397 5.64999 21.0422C6.68749 21.2997 7.71499 20.6297 7.98999 19.6222V19.5822C8.16999 18.5972 7.56999 17.5097 6.48999 17.3022C6.47249 17.2972 6.46749 17.2872 6.44999 17.2822C6.07249 17.1822 5.78249 16.9747 5.58999 16.7822C5.50249 16.6947 5.43249 16.6247 5.38999 16.5622L5.36999 16.5222C5.36249 16.5097 5.35499 16.5122 5.34999 16.5022C5.34749 16.4972 5.35249 16.4872 5.34999 16.4822C5.34249 16.4622 5.32999 16.4597 5.32999 16.4622L5.30999 16.4222C5.07249 15.9497 4.67999 15.6197 4.20999 15.4622C3.98249 15.3872 3.73499 15.3647 3.48999 15.3822ZM3.56999 16.6222C3.65499 16.6172 3.73249 16.6372 3.80999 16.6622C3.96499 16.7147 4.08749 16.8347 4.16999 17.0022C4.17999 17.0222 4.19999 17.0447 4.20999 17.0622C4.23999 17.1222 4.26499 17.1772 4.30999 17.2422C4.39749 17.3722 4.52499 17.5372 4.68999 17.7022C5.01749 18.0297 5.50499 18.3822 6.18999 18.5422H6.20999L6.24999 18.5622C6.65249 18.6297 6.79749 18.9072 6.74999 19.2822C6.74999 19.2897 6.74999 19.2947 6.74999 19.3022C6.63749 19.6797 6.27999 19.8972 5.92999 19.8022H5.90999L5.88999 19.7822C4.21999 19.4472 3.25749 18.1772 2.94999 17.5622L2.92999 17.5422L2.90999 17.5022C2.92249 17.5222 2.89999 17.4047 2.88999 17.3022C2.92749 17.0322 3.07249 16.8072 3.26999 16.7222L3.28999 16.7022H3.30999C3.39249 16.6597 3.48499 16.6272 3.56999 16.6222ZM15.69 21.1222C14.6375 21.1222 13.77 21.9897 13.77 23.0422C13.77 24.0947 14.6375 24.9622 15.69 24.9622C16.7425 24.9622 17.61 24.0947 17.61 23.0422C17.61 21.9897 16.7425 21.1222 15.69 21.1222ZM15.69 22.4022C16.05 22.4022 16.33 22.6822 16.33 23.0422C16.33 23.4022 16.05 23.6822 15.69 23.6822C15.33 23.6822 15.05 23.4022 15.05 23.0422C15.05 22.6822 15.33 22.4022 15.69 22.4022ZM28.39 22.4022C27.4925 22.4347 26.7525 23.1297 26.59 24.0222H26.57C26.385 25.0997 26.15 25.8822 25.95 26.3822C25.85 26.6322 25.7725 26.8047 25.71 26.9222C25.68 26.9797 25.645 27.0197 25.63 27.0422L25.59 27.1022C25.025 27.9522 25.23 29.1247 26.05 29.7622C26.415 30.0372 26.8475 30.1422 27.21 30.1422C27.7875 30.1422 28.3175 29.8822 28.69 29.4422C28.695 29.4347 28.705 29.4297 28.71 29.4222C28.7175 29.4147 28.7225 29.4097 28.73 29.4022C28.87 29.2547 28.8975 29.1597 29.01 28.9622C29.1325 28.7422 29.29 28.4472 29.45 28.0622C29.7725 27.2897 30.145 26.1672 30.41 24.6822V24.6222C30.4975 23.5797 29.8175 22.6097 28.79 22.4222C28.6575 22.3972 28.5175 22.3972 28.39 22.4022ZM28.53 23.6622H28.57C28.94 23.7297 29.1525 24.0197 29.13 24.4822C28.8825 25.8647 28.555 26.8822 28.27 27.5622C28.1275 27.9047 27.9875 28.1722 27.89 28.3422C27.8175 28.4697 27.74 28.5822 27.75 28.5822L27.73 28.6022C27.59 28.7747 27.38 28.8622 27.21 28.8622C27.06 28.8622 26.9775 28.8522 26.83 28.7422C26.4975 28.4847 26.4475 28.1247 26.65 27.8222C26.665 27.7997 26.6925 27.7497 26.71 27.7222C26.745 27.6672 26.785 27.6047 26.83 27.5222C26.9175 27.3597 27.0325 27.1347 27.15 26.8422C27.3825 26.2547 27.65 25.4047 27.85 24.2422H27.83C27.8975 23.8697 28.205 23.6272 28.53 23.6622ZM5.44999 26.8822C4.39749 26.8822 3.52999 27.7497 3.52999 28.8022C3.52999 29.8547 4.39749 30.7222 5.44999 30.7222C6.50249 30.7222 7.36999 29.8547 7.36999 28.8022C7.36999 27.7497 6.50249 26.8822 5.44999 26.8822ZM5.44999 28.1622C5.80999 28.1622 6.08999 28.4422 6.08999 28.8022C6.08999 29.1622 5.80999 29.4422 5.44999 29.4422C5.08999 29.4422 4.80999 29.1622 4.80999 28.8022C4.80999 28.4422 5.08999 28.1622 5.44999 28.1622Z'
								stroke='currentColor'
								strokeWidth='0.6'
								mask='url(#path-1-outside-1_3_505)'
							/>
						</g>
						<defs>
							<clipPath id='clip0_3_505'>
								<rect
									width='32'
									height='32'
									fill='white'
									transform='translate(0.329956 0.00244141)'
								/>
							</clipPath>
						</defs>
					</svg>
				),
				sources: [sources.CMS],
			},
			{
				label: 'Death Rate for Heart Attack Patients',
				getValue: (facility) =>
					facility.cmsFacility?.metrics.find(
						(el) => el.measureId === CmsMetric.AMI,
					)?.score,
				isFeatured: checkNullUndefined,
				formatter: (value) =>
					value != null
						? `${(typeof value === 'string'
								? Number.parseFloat(value)
								: value
						  ).toLocaleString()}%`
						: '',
				customIcon: (
					<svg
						width='33'
						height='33'
						viewBox='0 0 33 33'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<g clipPath='url(#clip0_3_957)'>
							<path
								d='M21.7702 10.0835L21.7737 10.2335L21.7701 10.0835C21.5623 10.0884 21.3648 10.1751 21.2204 10.3246C21.22 10.325 21.2197 10.3254 21.2193 10.3258L16.6599 14.8851L12.1 10.3251C12.0997 10.3249 12.0995 10.3246 12.0992 10.3244C12.0257 10.249 11.938 10.1892 11.841 10.1482C11.7438 10.1071 11.6393 10.086 11.5337 10.086H11.5337C11.3765 10.086 11.2229 10.1329 11.0926 10.2207C10.9622 10.3085 10.861 10.4331 10.8019 10.5787C10.7427 10.7243 10.7284 10.8843 10.7606 11.0381C10.7928 11.1916 10.8699 11.332 10.9821 11.4415C10.9823 11.4418 10.9826 11.442 10.9828 11.4422L15.5428 16.0022L10.9824 20.5626C10.9071 20.6352 10.847 20.722 10.8055 20.818C10.7638 20.9145 10.7418 21.0183 10.7407 21.1234C10.7396 21.2285 10.7595 21.3328 10.7993 21.4301C10.839 21.5274 10.8977 21.6158 10.9721 21.6901C11.0464 21.7644 11.1348 21.8232 11.2321 21.8629C11.3294 21.9026 11.4337 21.9225 11.5388 21.9215C11.6439 21.9204 11.7477 21.8984 11.8442 21.8567C11.9402 21.8152 12.027 21.755 12.0995 21.6798L16.6599 17.1194L21.2204 21.6798C21.2929 21.755 21.3797 21.8152 21.4757 21.8567C21.5722 21.8984 21.676 21.9204 21.7811 21.9215C21.8862 21.9225 21.9905 21.9026 22.0878 21.8629C22.1851 21.8232 22.2735 21.7644 22.3478 21.6901C22.4221 21.6158 22.4809 21.5274 22.5206 21.4301C22.5603 21.3328 22.5802 21.2285 22.5792 21.1234C22.5781 21.0183 22.5561 20.9145 22.5144 20.818C22.4729 20.722 22.4128 20.6352 22.3375 20.5626L17.7771 16.0022L22.3364 11.4428C22.3368 11.4425 22.3371 11.4422 22.3375 11.4418C22.4516 11.3316 22.53 11.1896 22.5623 11.0343C22.5947 10.8785 22.5793 10.7165 22.5181 10.5696C22.4569 10.4226 22.3527 10.2976 22.2193 10.2109C22.0858 10.1242 21.9293 10.0798 21.7702 10.0835ZM16.6599 1.13223C8.4552 1.13223 1.78994 7.79749 1.78994 16.0022C1.78994 24.207 8.4552 30.8722 16.6599 30.8722C24.8647 30.8722 31.5299 24.207 31.5299 16.0022C31.5299 7.79749 24.8647 1.13223 16.6599 1.13223ZM16.6599 2.71223C24.0072 2.71223 29.9499 8.65497 29.9499 16.0022C29.9499 23.3495 24.0072 29.2922 16.6599 29.2922C9.31268 29.2922 3.36994 23.3495 3.36994 16.0022C3.36994 8.65497 9.31268 2.71223 16.6599 2.71223Z'
								fill='currentColor'
								stroke='currentColor'
								strokeWidth='0.3'
							/>
						</g>
						<defs>
							<clipPath id='clip0_3_957'>
								<rect
									width='32'
									height='32'
									fill='white'
									transform='translate(0.659912 0.00244141)'
								/>
							</clipPath>
						</defs>
					</svg>
				),
				sources: [sources.CMS],
			},
			{
				label:
					'Death Rate for Chronic Obstructive Pulmonary Disease (COPD) Patients',
				getValue: (facility) =>
					facility.cmsFacility?.metrics.find(
						(el) => el.measureId === CmsMetric.COPD,
					)?.score,
				isFeatured: checkNullUndefined,
				formatter: (value) =>
					value != null
						? `${(typeof value === 'string'
								? Number.parseFloat(value)
								: value
						  ).toLocaleString()}%`
						: '',
				customIcon: (
					<svg
						width='33'
						height='33'
						viewBox='0 0 33 33'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<g clipPath='url(#clip0_3_957)'>
							<path
								d='M21.7702 10.0835L21.7737 10.2335L21.7701 10.0835C21.5623 10.0884 21.3648 10.1751 21.2204 10.3246C21.22 10.325 21.2197 10.3254 21.2193 10.3258L16.6599 14.8851L12.1 10.3251C12.0997 10.3249 12.0995 10.3246 12.0992 10.3244C12.0257 10.249 11.938 10.1892 11.841 10.1482C11.7438 10.1071 11.6393 10.086 11.5337 10.086H11.5337C11.3765 10.086 11.2229 10.1329 11.0926 10.2207C10.9622 10.3085 10.861 10.4331 10.8019 10.5787C10.7427 10.7243 10.7284 10.8843 10.7606 11.0381C10.7928 11.1916 10.8699 11.332 10.9821 11.4415C10.9823 11.4418 10.9826 11.442 10.9828 11.4422L15.5428 16.0022L10.9824 20.5626C10.9071 20.6352 10.847 20.722 10.8055 20.818C10.7638 20.9145 10.7418 21.0183 10.7407 21.1234C10.7396 21.2285 10.7595 21.3328 10.7993 21.4301C10.839 21.5274 10.8977 21.6158 10.9721 21.6901C11.0464 21.7644 11.1348 21.8232 11.2321 21.8629C11.3294 21.9026 11.4337 21.9225 11.5388 21.9215C11.6439 21.9204 11.7477 21.8984 11.8442 21.8567C11.9402 21.8152 12.027 21.755 12.0995 21.6798L16.6599 17.1194L21.2204 21.6798C21.2929 21.755 21.3797 21.8152 21.4757 21.8567C21.5722 21.8984 21.676 21.9204 21.7811 21.9215C21.8862 21.9225 21.9905 21.9026 22.0878 21.8629C22.1851 21.8232 22.2735 21.7644 22.3478 21.6901C22.4221 21.6158 22.4809 21.5274 22.5206 21.4301C22.5603 21.3328 22.5802 21.2285 22.5792 21.1234C22.5781 21.0183 22.5561 20.9145 22.5144 20.818C22.4729 20.722 22.4128 20.6352 22.3375 20.5626L17.7771 16.0022L22.3364 11.4428C22.3368 11.4425 22.3371 11.4422 22.3375 11.4418C22.4516 11.3316 22.53 11.1896 22.5623 11.0343C22.5947 10.8785 22.5793 10.7165 22.5181 10.5696C22.4569 10.4226 22.3527 10.2976 22.2193 10.2109C22.0858 10.1242 21.9293 10.0798 21.7702 10.0835ZM16.6599 1.13223C8.4552 1.13223 1.78994 7.79749 1.78994 16.0022C1.78994 24.207 8.4552 30.8722 16.6599 30.8722C24.8647 30.8722 31.5299 24.207 31.5299 16.0022C31.5299 7.79749 24.8647 1.13223 16.6599 1.13223ZM16.6599 2.71223C24.0072 2.71223 29.9499 8.65497 29.9499 16.0022C29.9499 23.3495 24.0072 29.2922 16.6599 29.2922C9.31268 29.2922 3.36994 23.3495 3.36994 16.0022C3.36994 8.65497 9.31268 2.71223 16.6599 2.71223Z'
								fill='currentColor'
								stroke='currentColor'
								strokeWidth='0.3'
							/>
						</g>
						<defs>
							<clipPath id='clip0_3_957'>
								<rect
									width='32'
									height='32'
									fill='white'
									transform='translate(0.659912 0.00244141)'
								/>
							</clipPath>
						</defs>
					</svg>
				),
				sources: [sources.CMS],
			},
			{
				label: 'Death Rate for Stroke Patients',
				getValue: (facility) =>
					facility.cmsFacility?.metrics.find(
						(el) => el.measureId === CmsMetric.STROKE,
					)?.score,
				isFeatured: checkNullUndefined,
				formatter: (value) =>
					value != null
						? `${(typeof value === 'string'
								? Number.parseFloat(value)
								: value
						  ).toLocaleString()}%`
						: '',
				customIcon: (
					<svg
						width='33'
						height='33'
						viewBox='0 0 33 33'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<g clipPath='url(#clip0_3_957)'>
							<path
								d='M21.7702 10.0835L21.7737 10.2335L21.7701 10.0835C21.5623 10.0884 21.3648 10.1751 21.2204 10.3246C21.22 10.325 21.2197 10.3254 21.2193 10.3258L16.6599 14.8851L12.1 10.3251C12.0997 10.3249 12.0995 10.3246 12.0992 10.3244C12.0257 10.249 11.938 10.1892 11.841 10.1482C11.7438 10.1071 11.6393 10.086 11.5337 10.086H11.5337C11.3765 10.086 11.2229 10.1329 11.0926 10.2207C10.9622 10.3085 10.861 10.4331 10.8019 10.5787C10.7427 10.7243 10.7284 10.8843 10.7606 11.0381C10.7928 11.1916 10.8699 11.332 10.9821 11.4415C10.9823 11.4418 10.9826 11.442 10.9828 11.4422L15.5428 16.0022L10.9824 20.5626C10.9071 20.6352 10.847 20.722 10.8055 20.818C10.7638 20.9145 10.7418 21.0183 10.7407 21.1234C10.7396 21.2285 10.7595 21.3328 10.7993 21.4301C10.839 21.5274 10.8977 21.6158 10.9721 21.6901C11.0464 21.7644 11.1348 21.8232 11.2321 21.8629C11.3294 21.9026 11.4337 21.9225 11.5388 21.9215C11.6439 21.9204 11.7477 21.8984 11.8442 21.8567C11.9402 21.8152 12.027 21.755 12.0995 21.6798L16.6599 17.1194L21.2204 21.6798C21.2929 21.755 21.3797 21.8152 21.4757 21.8567C21.5722 21.8984 21.676 21.9204 21.7811 21.9215C21.8862 21.9225 21.9905 21.9026 22.0878 21.8629C22.1851 21.8232 22.2735 21.7644 22.3478 21.6901C22.4221 21.6158 22.4809 21.5274 22.5206 21.4301C22.5603 21.3328 22.5802 21.2285 22.5792 21.1234C22.5781 21.0183 22.5561 20.9145 22.5144 20.818C22.4729 20.722 22.4128 20.6352 22.3375 20.5626L17.7771 16.0022L22.3364 11.4428C22.3368 11.4425 22.3371 11.4422 22.3375 11.4418C22.4516 11.3316 22.53 11.1896 22.5623 11.0343C22.5947 10.8785 22.5793 10.7165 22.5181 10.5696C22.4569 10.4226 22.3527 10.2976 22.2193 10.2109C22.0858 10.1242 21.9293 10.0798 21.7702 10.0835ZM16.6599 1.13223C8.4552 1.13223 1.78994 7.79749 1.78994 16.0022C1.78994 24.207 8.4552 30.8722 16.6599 30.8722C24.8647 30.8722 31.5299 24.207 31.5299 16.0022C31.5299 7.79749 24.8647 1.13223 16.6599 1.13223ZM16.6599 2.71223C24.0072 2.71223 29.9499 8.65497 29.9499 16.0022C29.9499 23.3495 24.0072 29.2922 16.6599 29.2922C9.31268 29.2922 3.36994 23.3495 3.36994 16.0022C3.36994 8.65497 9.31268 2.71223 16.6599 2.71223Z'
								fill='currentColor'
								stroke='currentColor'
								strokeWidth='0.3'
							/>
						</g>
						<defs>
							<clipPath id='clip0_3_957'>
								<rect
									width='32'
									height='32'
									fill='white'
									transform='translate(0.659912 0.00244141)'
								/>
							</clipPath>
						</defs>
					</svg>
				),
				sources: [sources.CMS],
			},
		],
	},
	// {
	// 	section: 'Beds & Utilization',
	// 	items: [
	// 		{
	// 			label: 'Total Hospital Beds',
	// 			getValue: (facility) => facility.ahaFacility?.svc1?.hospBeds,
	// 			isFeatured: checkNullUndefinedZero,
	// 			formatter: formatInt,
	// 			customIcon: (
	// 				<svg
	// 					width='23'
	// 					height='30'
	// 					viewBox='0 0 23 30'
	// 					fill='none'
	// 					xmlns='http://www.w3.org/2000/svg'
	// 				>
	// 					<path
	// 						fillRule='evenodd'
	// 						clipRule='evenodd'
	// 						d='M17.6458 2.42496H5.1198V11.4774H6.17169V11.4765V8.2135C6.17169 6.87639 7.25563 5.79245 8.59274 5.79245H14.1715C15.5086 5.79245 16.5926 6.87639 16.5926 8.2135V11.4765V11.4774H17.6458L17.6458 2.42496ZM5.1198 0.00390625C3.78269 0.00390625 2.69875 1.08785 2.69875 2.42496V11.4774V11.5851C1.41013 11.6398 0.382213 12.7018 0.382213 14.0039V21.6506C0.348233 21.7623 0.329956 21.8808 0.329956 22.0035V28.3719C0.329956 29.0404 0.871927 29.5824 1.54048 29.5824C2.20904 29.5824 2.75101 29.0404 2.75101 28.3719V24.3195H2.80327H19.9608H20.0131V28.3719C20.0131 29.0404 20.555 29.5824 21.2236 29.5824C21.8921 29.5824 22.4341 29.0404 22.4341 28.3719V22.0035C22.4341 21.8808 22.4158 21.7623 22.3819 21.6506V14.0039C22.3819 12.7024 21.3548 11.6407 20.0669 11.5851V11.4774V2.42496C20.0669 1.08785 18.983 0.00390625 17.6458 0.00390625H5.1198ZM19.9608 14.0039H2.80327V21.8984H19.9608V14.0039ZM8.59274 8.2135H14.1715L14.1715 11.4765H8.59274V8.2135Z'
	// 						fill='currentColor'
	// 					/>
	// 				</svg>
	// 			),
	// 			sources: [sources.AHA],
	// 		},
	// 		{
	// 			label: 'General Medical and Surgical Adult Beds',
	// 			getValue: (facility) => facility.ahaFacility?.svc1?.genBeds,
	// 			isFeatured: checkNullUndefinedZero,
	// 			formatter: formatInt,
	// 			customIcon: (
	// 				<svg
	// 					width='23'
	// 					height='30'
	// 					viewBox='0 0 23 30'
	// 					fill='none'
	// 					xmlns='http://www.w3.org/2000/svg'
	// 				>
	// 					<path
	// 						fillRule='evenodd'
	// 						clipRule='evenodd'
	// 						d='M17.6458 2.42496H5.1198V11.4774H6.17169V11.4765V8.2135C6.17169 6.87639 7.25563 5.79245 8.59274 5.79245H14.1715C15.5086 5.79245 16.5926 6.87639 16.5926 8.2135V11.4765V11.4774H17.6458L17.6458 2.42496ZM5.1198 0.00390625C3.78269 0.00390625 2.69875 1.08785 2.69875 2.42496V11.4774V11.5851C1.41013 11.6398 0.382213 12.7018 0.382213 14.0039V21.6506C0.348233 21.7623 0.329956 21.8808 0.329956 22.0035V28.3719C0.329956 29.0404 0.871927 29.5824 1.54048 29.5824C2.20904 29.5824 2.75101 29.0404 2.75101 28.3719V24.3195H2.80327H19.9608H20.0131V28.3719C20.0131 29.0404 20.555 29.5824 21.2236 29.5824C21.8921 29.5824 22.4341 29.0404 22.4341 28.3719V22.0035C22.4341 21.8808 22.4158 21.7623 22.3819 21.6506V14.0039C22.3819 12.7024 21.3548 11.6407 20.0669 11.5851V11.4774V2.42496C20.0669 1.08785 18.983 0.00390625 17.6458 0.00390625H5.1198ZM19.9608 14.0039H2.80327V21.8984H19.9608V14.0039ZM8.59274 8.2135H14.1715L14.1715 11.4765H8.59274V8.2135Z'
	// 						fill='currentColor'
	// 					/>
	// 				</svg>
	// 			),
	// 			sources: [sources.AHA],
	// 		},
	// 		{
	// 			label: 'General Medical and Surgical Pediatric Beds',
	// 			getValue: (facility) => facility.ahaFacility?.svc1?.genPedBeds,
	// 			isFeatured: checkNullUndefinedZero,
	// 			formatter: formatInt,
	// 			customIcon: (
	// 				<svg
	// 					width='23'
	// 					height='30'
	// 					viewBox='0 0 23 30'
	// 					fill='none'
	// 					xmlns='http://www.w3.org/2000/svg'
	// 				>
	// 					<path
	// 						fillRule='evenodd'
	// 						clipRule='evenodd'
	// 						d='M17.6458 2.42496H5.1198V11.4774H6.17169V11.4765V8.2135C6.17169 6.87639 7.25563 5.79245 8.59274 5.79245H14.1715C15.5086 5.79245 16.5926 6.87639 16.5926 8.2135V11.4765V11.4774H17.6458L17.6458 2.42496ZM5.1198 0.00390625C3.78269 0.00390625 2.69875 1.08785 2.69875 2.42496V11.4774V11.5851C1.41013 11.6398 0.382213 12.7018 0.382213 14.0039V21.6506C0.348233 21.7623 0.329956 21.8808 0.329956 22.0035V28.3719C0.329956 29.0404 0.871927 29.5824 1.54048 29.5824C2.20904 29.5824 2.75101 29.0404 2.75101 28.3719V24.3195H2.80327H19.9608H20.0131V28.3719C20.0131 29.0404 20.555 29.5824 21.2236 29.5824C21.8921 29.5824 22.4341 29.0404 22.4341 28.3719V22.0035C22.4341 21.8808 22.4158 21.7623 22.3819 21.6506V14.0039C22.3819 12.7024 21.3548 11.6407 20.0669 11.5851V11.4774V2.42496C20.0669 1.08785 18.983 0.00390625 17.6458 0.00390625H5.1198ZM19.9608 14.0039H2.80327V21.8984H19.9608V14.0039ZM8.59274 8.2135H14.1715L14.1715 11.4765H8.59274V8.2135Z'
	// 						fill='currentColor'
	// 					/>
	// 				</svg>
	// 			),
	// 			sources: [sources.AHA],
	// 		},
	// 		{
	// 			label: 'Medical/Surgical Intensive Care Beds',
	// 			getValue: (facility) => facility.ahaFacility?.svc1?.msicBeds,
	// 			isFeatured: checkNullUndefinedZero,
	// 			formatter: formatInt,
	// 			customIcon: (
	// 				<svg
	// 					width='23'
	// 					height='30'
	// 					viewBox='0 0 23 30'
	// 					fill='none'
	// 					xmlns='http://www.w3.org/2000/svg'
	// 				>
	// 					<path
	// 						fillRule='evenodd'
	// 						clipRule='evenodd'
	// 						d='M17.6458 2.42496H5.1198V11.4774H6.17169V11.4765V8.2135C6.17169 6.87639 7.25563 5.79245 8.59274 5.79245H14.1715C15.5086 5.79245 16.5926 6.87639 16.5926 8.2135V11.4765V11.4774H17.6458L17.6458 2.42496ZM5.1198 0.00390625C3.78269 0.00390625 2.69875 1.08785 2.69875 2.42496V11.4774V11.5851C1.41013 11.6398 0.382213 12.7018 0.382213 14.0039V21.6506C0.348233 21.7623 0.329956 21.8808 0.329956 22.0035V28.3719C0.329956 29.0404 0.871927 29.5824 1.54048 29.5824C2.20904 29.5824 2.75101 29.0404 2.75101 28.3719V24.3195H2.80327H19.9608H20.0131V28.3719C20.0131 29.0404 20.555 29.5824 21.2236 29.5824C21.8921 29.5824 22.4341 29.0404 22.4341 28.3719V22.0035C22.4341 21.8808 22.4158 21.7623 22.3819 21.6506V14.0039C22.3819 12.7024 21.3548 11.6407 20.0669 11.5851V11.4774V2.42496C20.0669 1.08785 18.983 0.00390625 17.6458 0.00390625H5.1198ZM19.9608 14.0039H2.80327V21.8984H19.9608V14.0039ZM8.59274 8.2135H14.1715L14.1715 11.4765H8.59274V8.2135Z'
	// 						fill='currentColor'
	// 					/>
	// 				</svg>
	// 			),
	// 			sources: [sources.AHA],
	// 		},
	// 		{
	// 			label: 'Pediatric Intensive Care Beds',
	// 			getValue: (facility) => facility.ahaFacility?.svc1?.pedicBeds,
	// 			isFeatured: checkNullUndefinedZero,
	// 			formatter: formatInt,
	// 			customIcon: (
	// 				<svg
	// 					width='23'
	// 					height='30'
	// 					viewBox='0 0 23 30'
	// 					fill='none'
	// 					xmlns='http://www.w3.org/2000/svg'
	// 				>
	// 					<path
	// 						fillRule='evenodd'
	// 						clipRule='evenodd'
	// 						d='M17.6458 2.42496H5.1198V11.4774H6.17169V11.4765V8.2135C6.17169 6.87639 7.25563 5.79245 8.59274 5.79245H14.1715C15.5086 5.79245 16.5926 6.87639 16.5926 8.2135V11.4765V11.4774H17.6458L17.6458 2.42496ZM5.1198 0.00390625C3.78269 0.00390625 2.69875 1.08785 2.69875 2.42496V11.4774V11.5851C1.41013 11.6398 0.382213 12.7018 0.382213 14.0039V21.6506C0.348233 21.7623 0.329956 21.8808 0.329956 22.0035V28.3719C0.329956 29.0404 0.871927 29.5824 1.54048 29.5824C2.20904 29.5824 2.75101 29.0404 2.75101 28.3719V24.3195H2.80327H19.9608H20.0131V28.3719C20.0131 29.0404 20.555 29.5824 21.2236 29.5824C21.8921 29.5824 22.4341 29.0404 22.4341 28.3719V22.0035C22.4341 21.8808 22.4158 21.7623 22.3819 21.6506V14.0039C22.3819 12.7024 21.3548 11.6407 20.0669 11.5851V11.4774V2.42496C20.0669 1.08785 18.983 0.00390625 17.6458 0.00390625H5.1198ZM19.9608 14.0039H2.80327V21.8984H19.9608V14.0039ZM8.59274 8.2135H14.1715L14.1715 11.4765H8.59274V8.2135Z'
	// 						fill='currentColor'
	// 					/>
	// 				</svg>
	// 			),
	// 			sources: [sources.AHA],
	// 		},
	// 		{
	// 			label: 'Burn Care Beds',
	// 			getValue: (facility) => facility.ahaFacility?.svc1?.burnBeds,
	// 			isFeatured: checkNullUndefinedZero,
	// 			formatter: formatInt,
	// 			customIcon: (
	// 				<svg
	// 					width='23'
	// 					height='30'
	// 					viewBox='0 0 23 30'
	// 					fill='none'
	// 					xmlns='http://www.w3.org/2000/svg'
	// 				>
	// 					<path
	// 						fillRule='evenodd'
	// 						clipRule='evenodd'
	// 						d='M17.6458 2.42496H5.1198V11.4774H6.17169V11.4765V8.2135C6.17169 6.87639 7.25563 5.79245 8.59274 5.79245H14.1715C15.5086 5.79245 16.5926 6.87639 16.5926 8.2135V11.4765V11.4774H17.6458L17.6458 2.42496ZM5.1198 0.00390625C3.78269 0.00390625 2.69875 1.08785 2.69875 2.42496V11.4774V11.5851C1.41013 11.6398 0.382213 12.7018 0.382213 14.0039V21.6506C0.348233 21.7623 0.329956 21.8808 0.329956 22.0035V28.3719C0.329956 29.0404 0.871927 29.5824 1.54048 29.5824C2.20904 29.5824 2.75101 29.0404 2.75101 28.3719V24.3195H2.80327H19.9608H20.0131V28.3719C20.0131 29.0404 20.555 29.5824 21.2236 29.5824C21.8921 29.5824 22.4341 29.0404 22.4341 28.3719V22.0035C22.4341 21.8808 22.4158 21.7623 22.3819 21.6506V14.0039C22.3819 12.7024 21.3548 11.6407 20.0669 11.5851V11.4774V2.42496C20.0669 1.08785 18.983 0.00390625 17.6458 0.00390625H5.1198ZM19.9608 14.0039H2.80327V21.8984H19.9608V14.0039ZM8.59274 8.2135H14.1715L14.1715 11.4765H8.59274V8.2135Z'
	// 						fill='currentColor'
	// 					/>
	// 				</svg>
	// 			),
	// 			sources: [sources.AHA],
	// 		},
	// 	],
	// },
	// {
	// 	section: 'Mental Health',
	// 	items: [
	// 		{
	// 			label: 'Psychiatric Beds',
	// 			getValue: (facility) => facility.ahaFacility?.svc1?.psychBeds,
	// 			isFeatured: checkNullUndefinedZero,
	// 			formatter: formatInt,
	// 			customIcon: (
	// 				<svg
	// 					width='23'
	// 					height='30'
	// 					viewBox='0 0 23 30'
	// 					fill='none'
	// 					xmlns='http://www.w3.org/2000/svg'
	// 				>
	// 					<path
	// 						fillRule='evenodd'
	// 						clipRule='evenodd'
	// 						d='M17.6458 2.42496H5.1198V11.4774H6.17169V11.4765V8.2135C6.17169 6.87639 7.25563 5.79245 8.59274 5.79245H14.1715C15.5086 5.79245 16.5926 6.87639 16.5926 8.2135V11.4765V11.4774H17.6458L17.6458 2.42496ZM5.1198 0.00390625C3.78269 0.00390625 2.69875 1.08785 2.69875 2.42496V11.4774V11.5851C1.41013 11.6398 0.382213 12.7018 0.382213 14.0039V21.6506C0.348233 21.7623 0.329956 21.8808 0.329956 22.0035V28.3719C0.329956 29.0404 0.871927 29.5824 1.54048 29.5824C2.20904 29.5824 2.75101 29.0404 2.75101 28.3719V24.3195H2.80327H19.9608H20.0131V28.3719C20.0131 29.0404 20.555 29.5824 21.2236 29.5824C21.8921 29.5824 22.4341 29.0404 22.4341 28.3719V22.0035C22.4341 21.8808 22.4158 21.7623 22.3819 21.6506V14.0039C22.3819 12.7024 21.3548 11.6407 20.0669 11.5851V11.4774V2.42496C20.0669 1.08785 18.983 0.00390625 17.6458 0.00390625H5.1198ZM19.9608 14.0039H2.80327V21.8984H19.9608V14.0039ZM8.59274 8.2135H14.1715L14.1715 11.4765H8.59274V8.2135Z'
	// 						fill='currentColor'
	// 					/>
	// 				</svg>
	// 			),
	// 			sources: [sources.AHA],
	// 		},
	// 		{
	// 			label: 'Pediatric Psychiatric Beds',
	// 			getValue: (facility) => facility.ahaFacility?.svc2?.psychPedBeds,
	// 			isFeatured: checkNullUndefinedZero,
	// 			formatter: formatInt,
	// 			customIcon: (
	// 				<svg
	// 					width='23'
	// 					height='30'
	// 					viewBox='0 0 23 30'
	// 					fill='none'
	// 					xmlns='http://www.w3.org/2000/svg'
	// 				>
	// 					<path
	// 						fillRule='evenodd'
	// 						clipRule='evenodd'
	// 						d='M17.6458 2.42496H5.1198V11.4774H6.17169V11.4765V8.2135C6.17169 6.87639 7.25563 5.79245 8.59274 5.79245H14.1715C15.5086 5.79245 16.5926 6.87639 16.5926 8.2135V11.4765V11.4774H17.6458L17.6458 2.42496ZM5.1198 0.00390625C3.78269 0.00390625 2.69875 1.08785 2.69875 2.42496V11.4774V11.5851C1.41013 11.6398 0.382213 12.7018 0.382213 14.0039V21.6506C0.348233 21.7623 0.329956 21.8808 0.329956 22.0035V28.3719C0.329956 29.0404 0.871927 29.5824 1.54048 29.5824C2.20904 29.5824 2.75101 29.0404 2.75101 28.3719V24.3195H2.80327H19.9608H20.0131V28.3719C20.0131 29.0404 20.555 29.5824 21.2236 29.5824C21.8921 29.5824 22.4341 29.0404 22.4341 28.3719V22.0035C22.4341 21.8808 22.4158 21.7623 22.3819 21.6506V14.0039C22.3819 12.7024 21.3548 11.6407 20.0669 11.5851V11.4774V2.42496C20.0669 1.08785 18.983 0.00390625 17.6458 0.00390625H5.1198ZM19.9608 14.0039H2.80327V21.8984H19.9608V14.0039ZM8.59274 8.2135H14.1715L14.1715 11.4765H8.59274V8.2135Z'
	// 						fill='currentColor'
	// 					/>
	// 				</svg>
	// 			),
	// 			sources: [sources.AHA],
	// 		},
	// 		{
	// 			label: 'Geriatric Psychiatric Beds',
	// 			getValue: (facility) => facility.ahaFacility?.svc2?.psychGerBeds,
	// 			isFeatured: checkNullUndefinedZero,
	// 			formatter: formatInt,
	// 			customIcon: (
	// 				<svg
	// 					width='23'
	// 					height='30'
	// 					viewBox='0 0 23 30'
	// 					fill='none'
	// 					xmlns='http://www.w3.org/2000/svg'
	// 				>
	// 					<path
	// 						fillRule='evenodd'
	// 						clipRule='evenodd'
	// 						d='M17.6458 2.42496H5.1198V11.4774H6.17169V11.4765V8.2135C6.17169 6.87639 7.25563 5.79245 8.59274 5.79245H14.1715C15.5086 5.79245 16.5926 6.87639 16.5926 8.2135V11.4765V11.4774H17.6458L17.6458 2.42496ZM5.1198 0.00390625C3.78269 0.00390625 2.69875 1.08785 2.69875 2.42496V11.4774V11.5851C1.41013 11.6398 0.382213 12.7018 0.382213 14.0039V21.6506C0.348233 21.7623 0.329956 21.8808 0.329956 22.0035V28.3719C0.329956 29.0404 0.871927 29.5824 1.54048 29.5824C2.20904 29.5824 2.75101 29.0404 2.75101 28.3719V24.3195H2.80327H19.9608H20.0131V28.3719C20.0131 29.0404 20.555 29.5824 21.2236 29.5824C21.8921 29.5824 22.4341 29.0404 22.4341 28.3719V22.0035C22.4341 21.8808 22.4158 21.7623 22.3819 21.6506V14.0039C22.3819 12.7024 21.3548 11.6407 20.0669 11.5851V11.4774V2.42496C20.0669 1.08785 18.983 0.00390625 17.6458 0.00390625H5.1198ZM19.9608 14.0039H2.80327V21.8984H19.9608V14.0039ZM8.59274 8.2135H14.1715L14.1715 11.4765H8.59274V8.2135Z'
	// 						fill='currentColor'
	// 					/>
	// 				</svg>
	// 			),
	// 			sources: [sources.AHA],
	// 		},
	// 		{
	// 			label: 'Psychiatric Emergency Services',
	// 			getValue: (facility) =>
	// 				facility.ahaFacility?.svc2?.pscyhEmergencyServices,
	// 			// May be 1 (yes), 0 (no), null (unknown), or undefined (no aha facility)
	// 			isFeatured: (value) => (value != null ? isAha1(value) : null),
	// 			formatter: format012,
	// 			customIcon: (
	// 				<PsychologyOutlined
	// 					sx={{
	// 						fontSize: '33px',
	// 					}}
	// 				/>
	// 			),
	// 			sources: [sources.AHA],
	// 		},
	// 		{
	// 			label: 'Psychiatric Outpatient Services',
	// 			getValue: (facility) => facility.ahaFacility?.svc1?.msicBeds,
	// 			// May be 1 (yes), 0 (no), null (unknown), or undefined (no aha facility)
	// 			isFeatured: (value) => (value != null ? isAha1(value) : null),
	// 			formatter: format012,
	// 			customIcon: (
	// 				<PsychologyOutlined
	// 					sx={{
	// 						fontSize: '33px',
	// 					}}
	// 				/>
	// 			),
	// 			sources: [sources.AHA],
	// 		},
	// 		{
	// 			label: 'Medical Board Without Intrusive Questions',
	// 			getValue: (facility) =>
	// 				INTRUSIVE_MENTAL_HEALTH_LICENSURE_STATES.includes(facility.state)
	// 					? 'Yes'
	// 					: 'No',
	// 			customIcon: (
	// 				<PsychologyOutlined
	// 					sx={{
	// 						fontSize: '33px',
	// 					}}
	// 				/>
	// 			),
	// 			sources: [sources.IVY],
	// 		},
	// 	],
	// },
];
